/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import API from "../api/axios";
import Presence from "../components/Presence";
import "../styles/motion.css";

/* ── FILE EXTENSION CONFIG ── */
const FILE_GROUPS = {
  "📄 Documents": [".pdf",".doc",".docx",".txt",".md",".csv",".xlsx",".pptx"],
  "💻 Code":      [".js",".ts",".jsx",".tsx",".py",".java",".cpp",".c",".go",".rs",".html",".css",".json",".yml",".sh"],
  "🖼️ Images":    [".png",".jpg",".jpeg",".gif",".webp",".svg"],
  "📦 Archives":  [".zip",".tar",".gz"],
};
const ALL_EXT = Object.values(FILE_GROUPS).flat();
const getFileType = (name="") => {
  const e = "."+name.split(".").pop().toLowerCase();
  if([".pdf",".doc",".docx",".txt",".md",".csv",".xlsx",".pptx"].includes(e)) return "doc";
  if([".js",".ts",".jsx",".tsx",".py",".java",".cpp",".c",".go",".rs",".html",".css",".json",".yml",".sh"].includes(e)) return "code";
  if([".png",".jpg",".jpeg",".gif",".webp",".svg"].includes(e)) return "img";
  return "other";
};
const FICON = {doc:"📄",code:"💻",img:"🖼️",other:"📦"};
const FBADGE = {doc:{bg:"var(--info-tint)",c:"var(--info)"},code:{bg:"var(--success-tint)",c:"var(--success)"},img:{bg:"var(--warning-tint)",c:"var(--warning)"},other:{bg:"var(--surface-2)",c:"var(--text-2)"}};

/* ── LANGUAGES ── */
const LANGS = [
  {code:"english",  native:"English",  flag:"🇬🇧"},
  {code:"hindi",    native:"हिंदी",      flag:"🇮🇳"},
  {code:"telugu",   native:"తెలుగు",     flag:"🇮🇳"},
  {code:"tamil",    native:"தமிழ்",      flag:"🇮🇳"},
  {code:"malayalam",native:"മലയാളം",    flag:"🇮🇳"},
];

/* ── STYLES ── */
const S = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
.vs-shell{display:flex;min-height:100vh;font-family:var(--font-body);background:var(--bg)}
/* SIDEBAR */
.vs-sidebar{width:236px;min-height:100vh;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;padding:var(--space-6) 0 var(--space-5);flex-shrink:0;position:sticky;top:0;height:100vh}
.vs-logo{font-family:var(--font-display);font-size:22px;font-weight:500;color:var(--text);letter-spacing:-.01em;padding:0 var(--space-5) var(--space-6)}
.vs-nav{display:flex;flex-direction:column;gap:2px;flex:1;padding:0 12px}
.vs-nav-btn{padding:10px 14px;border-radius:10px;font-size:13.5px;font-weight:500;color:var(--text-2);cursor:pointer;transition:all .18s;background:transparent;border:none;text-align:left;width:100%;display:flex;align-items:center;gap:10px;font-family:var(--font-body)}
.vs-nav-btn:hover{color:var(--text);background:var(--surface-2)}
.vs-nav-btn.active{background:var(--accent-tint);color:var(--accent);font-weight:600;box-shadow:inset 2px 0 0 var(--accent)}
.vs-nav-icon{font-size:15px;width:20px;text-align:center;flex-shrink:0}
.vs-nav-badge{margin-left:auto;background:var(--accent);color:var(--on-accent);border-radius:20px;padding:1px 7px;font-size:10px;font-weight:700}
.vs-sidebar-footer{margin:0 12px;padding:10px 14px;border-radius:10px;border:1px solid var(--border);display:flex;align-items:center;gap:10px;cursor:pointer;transition:background .18s}
.vs-sidebar-footer:hover{background:var(--surface-2)}
.vs-sidebar-avatar{width:28px;height:28px;border-radius:50%;background:var(--accent);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:var(--on-accent);flex-shrink:0}
.vs-sidebar-username{font-size:13px;color:var(--text-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1}
/* MAIN */
.vs-main{flex:1;background:var(--bg);min-height:100vh;padding:var(--space-6) var(--space-7);overflow-y:auto;animation:vsFadeIn .3s ease}
@keyframes vsFadeIn{from{opacity:0;transform:translateX(8px)}to{opacity:1;transform:translateX(0)}}
@keyframes vsSlideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes vsScaleIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}
/* TOPBAR */
.vs-topbar{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:26px}
.vs-page-title{font-family:var(--font-display);font-size:30px;font-weight:500;letter-spacing:-.02em;color:var(--text);line-height:1.1}
.vs-page-subtitle{font-size:14px;color:var(--text-3);margin-top:var(--space-2)}
.vs-topbar-actions{display:flex;align-items:center;gap:10px}
/* BUTTONS */
.vs-btn-primary{padding:10px 18px;background:var(--accent);color:var(--on-accent);border:1px solid var(--accent);border-radius:var(--radius);box-shadow:var(--shadow-sm);font-family:var(--font-body);font-size:13px;font-weight:600;cursor:pointer;transition:background .18s,transform .15s,box-shadow .18s;display:flex;align-items:center;gap:6px;white-space:nowrap}
.vs-btn-primary:hover{background:var(--accent-hover);transform:translateY(-1px);box-shadow:var(--shadow-md)}
.vs-btn-primary:disabled{opacity:.5;cursor:not-allowed;transform:none}
.vs-btn-ghost{padding:10px 18px;background:var(--surface);color:var(--text-2);border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);font-size:13px;font-weight:500;cursor:pointer;transition:all .18s}
.vs-btn-ghost:hover{background:var(--surface-2);color:var(--text)}
.vs-btn-sm{padding:5px 13px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;border:none;font-family:var(--font-body);transition:all .18s}
.vs-btn-edit{background:var(--surface-2);color:var(--text-2)}.vs-btn-edit:hover{background:var(--surface-3)}
.vs-btn-view{background:var(--info-tint);color:var(--info);border:1px solid var(--info-border)}.vs-btn-view:hover{background:var(--info-tint)}
.vs-btn-dl{background:var(--success-tint);color:var(--success);border:1px solid var(--success-border)}.vs-btn-dl:hover{background:var(--success-tint)}
.vs-btn-del{background:var(--danger-tint);color:var(--danger);border:1px solid var(--danger-border)}.vs-btn-del:hover{background:var(--danger-tint)}
.vs-btn-restore{background:var(--success-tint);color:var(--success);border:1px solid var(--success-border)}.vs-btn-restore:hover{background:var(--success-tint)}
.vs-btn-permdel{background:var(--danger-tint);color:var(--danger);border:1px solid var(--danger-border)}.vs-btn-permdel:hover{background:var(--danger-tint)}
.vs-btn-code{background:var(--success-tint);color:var(--success);border:1px solid var(--success-border)}.vs-btn-code:hover{background:var(--success-tint)}
/* FILTER */
.vs-filter-row{display:flex;gap:10px;margin-bottom:18px;align-items:center}
.vs-input{padding:11px 16px;background:var(--surface);border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);font-size:14px;color:var(--text);outline:none;transition:border-color .2s,background .2s;width:100%}
.vs-input::placeholder{color:var(--text-3)}
.vs-input:focus{border-color:var(--accent);box-shadow:var(--ring)}
.vs-select{padding:11px 38px 11px 16px;background-color:var(--surface);border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);font-size:14px;color:var(--text-2);outline:none;cursor:pointer;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%237a5a48' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center;transition:border-color .2s;flex-shrink:0;min-width:150px}
.vs-select:focus{border-color:var(--accent);box-shadow:var(--ring)}
/* PROJECT CARDS */
.vs-project-list{display:flex;flex-direction:column;gap:10px}
.vs-project-card{background:var(--surface);border-radius:16px;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;border-left:3px solid var(--border-strong);transition:transform var(--duration-fast) var(--ease-smooth-out),box-shadow var(--duration-fast) var(--ease-smooth-out),border-color var(--duration-fast) var(--ease-smooth-out);gap:14px;animation:vsSlideUp .3s ease backwards}
.vs-project-card:hover{border-left-color:var(--accent);box-shadow:var(--shadow-md);transform:translateY(var(--lift))}
.vs-project-info{flex:1;min-width:0}
.vs-project-name{font-size:15px;font-weight:600;color:var(--text);margin-bottom:3px;cursor:pointer}
.vs-project-name:hover{color:var(--accent);text-decoration:underline}
.vs-project-stack{font-size:12px;color:var(--text-3);margin-bottom:7px}
.vs-project-footer{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.vs-project-date{font-size:11px;color:var(--text-3)}
.vs-link-btn{font-size:12px;font-weight:600;color:var(--accent);background:none;border:none;padding:0;cursor:pointer;font-family:var(--font-body);transition:color .18s}
.vs-link-btn:hover{color:var(--text);text-decoration:underline}
.vs-file-chip{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:600}
.vs-project-right{display:flex;flex-direction:column;align-items:flex-end;gap:7px;flex-shrink:0}
.vs-tags{display:flex;gap:5px;flex-wrap:wrap;justify-content:flex-end}
.vs-tag{padding:3px 10px;background:var(--surface-2);border-radius:20px;font-size:11px;font-weight:500;color:var(--text-2)}
.vs-card-actions{display:flex;gap:5px;flex-wrap:wrap;justify-content:flex-end}
/* EMPTY */
.vs-empty{text-align:center;padding:60px 0;color:var(--text-3);font-size:14px}
.vs-empty-icon{font-size:40px;margin-bottom:12px;opacity:.5}
/* PAGINATION */
.vs-pagination{display:flex;justify-content:center;align-items:center;gap:12px;margin-top:22px}
.vs-page-btn{padding:8px 18px;background:var(--surface-2);border:none;border-radius:10px;color:var(--text-2);font-family:var(--font-body);font-size:13px;font-weight:500;cursor:pointer;transition:background .18s}
.vs-page-btn:hover:not(:disabled){background:var(--surface-3)}
.vs-page-btn:disabled{opacity:.35;cursor:not-allowed}
.vs-page-info{font-size:13px;color:var(--text-3)}
/* BIN */
.vs-bin-card{background:var(--surface);border-radius:14px;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;border-left:3px solid var(--border);gap:14px;opacity:.75;margin-bottom:10px;animation:vsSlideUp .3s ease both}
.vs-bin-name{font-size:15px;font-weight:600;color:var(--text-2);margin-bottom:3px}
.vs-bin-meta{font-size:12px;color:var(--text-3)}
.vs-bin-actions{display:flex;gap:6px;flex-shrink:0}
/* COLLAB */
.vs-collab-tabs{display:flex;gap:4px;margin-bottom:var(--space-5);background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius);padding:4px;width:fit-content}
.vs-collab-tab{padding:8px 20px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;border:none;font-family:var(--font-body);background:transparent;color:var(--text-3);transition:all .18s}
.vs-collab-tab.active{background:var(--surface);color:var(--text);box-shadow:var(--shadow-sm)}
.vs-collab-tab:not(.active):hover{color:var(--text)}
.vs-collab-layout{display:grid;grid-template-columns:1fr 1fr;gap:18px}
@media(max-width:860px){.vs-collab-layout{grid-template-columns:1fr}}
.vs-collab-panel{background:var(--surface);border-radius:18px;padding:24px;border:1px solid var(--border)}
.vs-panel-title{font-family:var(--font-display);font-size:18px;color:var(--text);margin-bottom:16px}
.vs-user-result{display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:12px;background:var(--surface-2);margin-bottom:8px;transition:background .18s;border:1px solid transparent}
.vs-user-result:hover{background:var(--surface-3);border-color:var(--accent-soft)}
.vs-user-avatar{width:34px;height:34px;border-radius:50%;background:var(--accent);color:var(--on-accent);font-weight:700;font-size:13px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.vs-invite-btn{padding:6px 14px;background:var(--accent);color:var(--on-accent);border:none;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;font-family:var(--font-body);transition:background .18s;flex-shrink:0}
.vs-invite-btn:hover{background:var(--accent-hover)}
.vs-invite-card{display:flex;align-items:center;gap:12px;padding:14px 16px;border-radius:14px;background:var(--surface-2);margin-bottom:10px;border:1px solid var(--border);animation:vsSlideUp .25s ease both}
.vs-invite-status{padding:4px 12px;border-radius:20px;font-size:11px;font-weight:700;white-space:nowrap;flex-shrink:0}
.vs-invite-status.pending{background:var(--warning-tint);color:var(--warning)}
.vs-invite-status.accepted{background:var(--success-tint);color:var(--success)}
.vs-invite-status.declined{background:var(--danger-tint);color:var(--danger)}
.vs-invite-actions{display:flex;gap:6px;flex-shrink:0}
.vs-btn-accept{padding:6px 14px;background:var(--success-tint);color:var(--success);border:1px solid var(--success-border);border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;font-family:var(--font-body);transition:all .18s}
.vs-btn-accept:hover{background:var(--success-tint)}
.vs-btn-decline{padding:6px 14px;background:var(--danger-tint);color:var(--danger);border:1px solid var(--danger-border);border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;font-family:var(--font-body);transition:all .18s}
.vs-btn-decline:hover{background:var(--danger-tint)}
.vs-section-label{font-size:11px;font-weight:700;color:var(--text-3);text-transform:uppercase;letter-spacing:.8px;margin:18px 0 10px}
/* SETTINGS */
.vs-settings-card{background:var(--surface);border-radius:18px;padding:26px;border:1px solid var(--border);margin-bottom:16px}
.vs-settings-title{font-family:var(--font-display);font-size:19px;color:var(--text);margin-bottom:16px;display:flex;align-items:center;gap:12px}
.vs-settings-line{flex:1;height:1px;background:linear-gradient(90deg,var(--border),transparent)}
.vs-lang-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:16px}
.vs-lang-option{padding:12px 16px;background:var(--surface-2);border:1.5px solid transparent;border-radius:12px;font-family:var(--font-body);font-size:14px;font-weight:500;color:var(--text-2);cursor:pointer;display:flex;align-items:center;gap:12px;transition:all .18s}
.vs-lang-option:hover{background:var(--surface-3);border-color:var(--accent-soft)}
.vs-lang-option.active{background:var(--accent);color:var(--on-accent);border-color:var(--accent)}
.vs-lang-check{margin-left:auto;font-size:14px}
/* MODAL */
.vs-modal-label{font-size:11px;font-weight:700;color:var(--text-3);text-transform:uppercase;letter-spacing:.7px;display:block;margin:10px 0 5px}
.vs-modal-overlay{position:fixed;inset:0;background:var(--overlay);backdrop-filter:blur(5px);z-index:300;display:flex;align-items:center;justify-content:center;animation:vsFadeIn .2s ease;padding:20px}
.vs-modal{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-xl);padding:var(--space-6);width:100%;max-width:540px;animation:vsScaleIn .22s ease;box-shadow:var(--shadow-lg);max-height:90vh;overflow-y:auto}
.vs-modal-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:22px}
.vs-modal-title{font-family:var(--font-display);font-size:22px;color:var(--text)}
.vs-modal-close{width:32px;height:32px;background:var(--surface-2);border:none;border-radius:8px;color:var(--text-2);font-size:15px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .18s}
.vs-modal-close:hover{background:var(--surface-3)}
.vs-modal-input{width:100%;padding:12px 16px;background:var(--surface);border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);font-size:14px;color:var(--text);outline:none;margin-bottom:4px;transition:border-color .2s,background .2s}
.vs-modal-input::placeholder{color:var(--text-3)}
.vs-modal-input:focus{border-color:var(--accent);box-shadow:var(--ring)}
.vs-modal-textarea{width:100%;padding:12px 16px;background:var(--surface);border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);font-size:14px;color:var(--text);outline:none;margin-bottom:4px;resize:vertical;min-height:80px;transition:border-color .2s,background .2s}
.vs-modal-textarea::placeholder{color:var(--text-3)}
.vs-modal-textarea:focus{border-color:var(--accent);box-shadow:var(--ring)}
/* EXT PICKER */
.vs-ext-group{margin-bottom:10px}
.vs-ext-group-label{font-size:11px;font-weight:700;color:var(--text-3);text-transform:uppercase;letter-spacing:.6px;margin:8px 0 6px;display:block}
.vs-ext-chips{display:flex;flex-wrap:wrap;gap:5px}
.vs-ext-chip{padding:4px 11px;border-radius:20px;font-size:11px;font-weight:600;cursor:pointer;border:1.5px solid var(--border-strong);background:var(--surface-2);color:var(--text-2);transition:all .15s;font-family:var(--font-body)}
.vs-ext-chip:hover{border-color:var(--accent);background:var(--surface)}
.vs-ext-chip.sel{background:var(--accent);color:var(--on-accent);border-color:var(--accent)}
/* UPLOAD AREA */
.vs-upload-area{border:2px dashed var(--border-strong);border-radius:12px;padding:18px;text-align:center;cursor:pointer;transition:border-color .2s,background .2s;margin-bottom:4px;position:relative}
.vs-upload-area:hover{border-color:var(--accent);background:var(--surface)}
.vs-upload-area input[type=file]{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}
.vs-upload-hint{font-size:12px;color:var(--text-3);margin-top:4px}
.vs-file-selected{font-size:12px;color:var(--accent);font-weight:600;margin-top:6px}
.vs-modal-btn-row{display:flex;gap:10px;margin-top:14px}
/* COMMENT */
.vs-comment{background:var(--surface-2);border-radius:12px;padding:12px 14px;margin-bottom:8px;animation:vsSlideUp .2s ease both}
.vs-comment-author{font-size:12px;font-weight:700;color:var(--text-2);margin-bottom:4px}
.vs-comment-text{font-size:13px;color:var(--text-2);line-height:1.6}
.vs-comment-date{font-size:11px;color:var(--text-3);margin-top:4px}
.vs-comment-input-row{display:flex;gap:8px;margin-top:12px}
/* VERSION HISTORY */
.vs-version-item{display:flex;align-items:flex-start;gap:12px;padding:12px 14px;border-radius:12px;background:var(--surface-2);margin-bottom:8px;border:1px solid var(--border);animation:vsSlideUp .2s ease both}
.vs-version-num{font-family:var(--font-display);font-size:18px;color:var(--accent);font-weight:700;flex-shrink:0;min-width:32px}
.vs-version-info{flex:1;min-width:0}
.vs-version-title{font-size:13px;font-weight:600;color:var(--text);margin-bottom:2px}
.vs-version-note{font-size:12px;color:var(--text-3);font-style:italic;margin-bottom:3px}
.vs-version-meta{font-size:11px;color:var(--text-3)}
.vs-version-restore-btn{padding:5px 12px;background:var(--accent-tint);color:var(--accent);border:1px solid var(--accent-soft);border-radius:8px;font-size:11px;font-weight:700;cursor:pointer;font-family:var(--font-body);transition:all .18s;flex-shrink:0}
.vs-version-restore-btn:hover{background:var(--accent-soft)}
.vs-drawer-tabs{display:flex;gap:3px;margin-bottom:18px;background:var(--surface-2);border-radius:12px;padding:4px}
.vs-drawer-tab{flex:1;padding:8px;border-radius:9px;font-size:12px;font-weight:600;cursor:pointer;border:none;font-family:var(--font-body);background:transparent;color:var(--text-3);transition:all .18s;text-align:center}
.vs-drawer-tab.active{background:var(--accent);color:var(--on-accent)}
.vs-drawer-tab:not(.active):hover{background:var(--surface-3);color:var(--text-2)}
/* DRAWER */
.vs-drawer-overlay{position:fixed;inset:0;background:var(--overlay);z-index:250;backdrop-filter:blur(3px);animation:vsFadeIn .2s ease}
.vs-drawer{position:fixed;right:0;top:0;bottom:0;width:500px;background:var(--bg);box-shadow:var(--shadow-lg);z-index:260;display:flex;flex-direction:column;animation:vsDrawerIn .28s ease;overflow:hidden}
@keyframes vsDrawerIn{from{transform:translateX(100%)}to{transform:translateX(0)}}
.vs-drawer-header{padding:22px 26px 18px;border-bottom:1px solid var(--border);flex-shrink:0;display:flex;justify-content:space-between;align-items:flex-start}
.vs-drawer-title{font-family:var(--font-display);font-size:22px;color:var(--text);flex:1;margin-right:12px}
.vs-drawer-body{flex:1;overflow-y:auto;padding:22px 26px}
.vs-drawer-section{margin-bottom:20px}
.vs-drawer-label{font-size:11px;font-weight:700;color:var(--text-3);text-transform:uppercase;letter-spacing:1px;margin-bottom:6px}
.vs-drawer-value{font-size:14px;color:var(--text-2);line-height:1.7}
.vs-drawer-footer{padding:16px 26px;border-top:1px solid var(--border);flex-shrink:0;display:flex;gap:10px;flex-wrap:wrap}
/* EDITOR */
.vs-editor-modal{background:var(--bg);border-radius:22px;width:100%;max-width:960px;animation:vsScaleIn .22s ease;box-shadow:var(--shadow-lg);height:88vh;overflow:hidden;display:flex;flex-direction:column}
.vs-editor-header{display:flex;justify-content:space-between;align-items:center;padding:18px 26px;border-bottom:1px solid var(--border);flex-shrink:0}
.vs-editor-title{font-family:var(--font-display);font-size:19px;color:var(--text)}
.vs-editor-meta{font-size:12px;color:var(--text-3);margin-top:2px}
.vs-editor-textarea{flex:1;padding:22px 26px;background:var(--surface);color:var(--text);caret-color:var(--accent);font-family:var(--font-mono);font-size:14px;line-height:1.75;border:none;outline:none;resize:none;height:100%;width:100%;box-sizing:border-box}
.vs-editor-textarea::placeholder{color:var(--text-3)}
.vs-editor-textarea::selection{background:var(--accent-soft);color:var(--text)}
.vs-editor-footer{display:flex;justify-content:space-between;align-items:center;padding:14px 26px;border-top:1px solid var(--border);flex-shrink:0;background:var(--bg)}
.vs-editor-info{font-size:12px;color:var(--text-3)}
/* IMG PREVIEW */
.vs-img-preview{flex:1;display:flex;align-items:center;justify-content:center;padding:24px;background:var(--bg);min-height:300px}
.vs-img-preview img{max-width:100%;max-height:400px;border-radius:10px;object-fit:contain}
/* ── STATS WIDGET ── */
.vs-stats-bar{display:grid;grid-template-columns:repeat(4,1fr);gap:var(--space-4);margin-bottom:var(--space-6)}
.vs-stat-widget{background:var(--surface);border-radius:var(--radius-lg);padding:var(--space-4) var(--space-5);border:1px solid var(--border);box-shadow:var(--shadow-xs);display:flex;align-items:center;gap:12px;animation:vsSlideUp .3s ease both}
.vs-stat-widget-icon{font-size:20px;flex-shrink:0;width:40px;height:40px;background:var(--accent-tint);border-radius:var(--radius);display:flex;align-items:center;justify-content:center}
.vs-stat-widget-num{font-family:var(--font-display);font-size:26px;font-weight:500;color:var(--text);line-height:1}
.vs-stat-widget-label{font-size:12.5px;color:var(--text-3);margin-top:var(--space-1)}
/* ── NOTIFICATIONS ── */
.vs-bell-wrap{position:relative;cursor:pointer;flex-shrink:0}
.vs-bell-btn{width:36px;height:36px;background:var(--surface-2);border:none;border-radius:10px;color:var(--text-2);font-size:17px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .18s;margin:0 12px 8px}
.vs-bell-btn:hover{background:var(--surface-3);color:var(--text)}
.vs-bell-dot{position:absolute;top:4px;right:14px;width:8px;height:8px;background:var(--accent);border-radius:50%;border:2px solid var(--surface);animation:vsPop .3s ease}
@keyframes vsPop{from{transform:scale(0)}to{transform:scale(1)}}
.vs-notif-panel{position:fixed;top:0;right:0;bottom:0;width:380px;background:var(--bg);box-shadow:var(--shadow-lg);z-index:270;display:flex;flex-direction:column;animation:vsDrawerIn .28s ease;overflow:hidden}
.vs-notif-header{padding:20px 22px 16px;border-bottom:1px solid var(--border);display:flex;justify-content:space-between;align-items:center;flex-shrink:0}
.vs-notif-title{font-family:var(--font-display);font-size:20px;color:var(--text)}
.vs-notif-actions{display:flex;gap:8px;align-items:center}
.vs-notif-clear-btn{font-size:12px;color:var(--text-3);background:none;border:none;cursor:pointer;padding:4px 8px;border-radius:6px;transition:background .18s;font-family:var(--font-body)}
.vs-notif-clear-btn:hover{background:var(--surface-3);color:var(--text-2)}
.vs-notif-list{flex:1;overflow-y:auto;padding:12px}
.vs-notif-item{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;border-radius:12px;margin-bottom:6px;cursor:pointer;transition:background .18s;border:1px solid transparent}
.vs-notif-item:hover{background:var(--surface-3);border-color:var(--border)}
.vs-notif-item.unread{background:var(--surface);border-color:var(--border)}
.vs-notif-item.unread::before{content:'';width:6px;height:6px;background:var(--accent);border-radius:50%;flex-shrink:0;margin-top:5px}
.vs-notif-icon{font-size:20px;flex-shrink:0;width:36px;height:36px;background:var(--surface-2);border-radius:10px;display:flex;align-items:center;justify-content:center}
.vs-notif-body{flex:1;min-width:0}
.vs-notif-item-title{font-size:13px;font-weight:600;color:var(--text);margin-bottom:2px}
.vs-notif-item-msg{font-size:12px;color:var(--text-3);line-height:1.5}
.vs-notif-item-time{font-size:11px;color:var(--text-3);margin-top:3px}
.vs-notif-empty{text-align:center;padding:48px 0;color:var(--text-3);font-size:14px}
/* ── GLOBAL SEARCH ── */
.vs-search-overlay{position:fixed;inset:0;background:var(--overlay);backdrop-filter:blur(4px);z-index:350;display:flex;align-items:flex-start;justify-content:center;padding-top:80px;animation:vsFadeIn .15s ease}
.vs-search-box{background:var(--bg);border-radius:20px;width:100%;max-width:600px;box-shadow:var(--shadow-lg);overflow:hidden;animation:vsScaleIn .18s ease}
.vs-search-input-row{display:flex;align-items:center;gap:12px;padding:18px 22px;border-bottom:1px solid var(--border)}
.vs-search-icon{font-size:18px;color:var(--text-3);flex-shrink:0}
.vs-search-field{flex:1;background:transparent;border:none;outline:none;font-family:var(--font-body);font-size:16px;color:var(--text)}
.vs-search-field::placeholder{color:var(--text-3)}
.vs-search-kbd{padding:3px 8px;background:var(--surface-2);border-radius:6px;font-size:11px;font-weight:700;color:var(--text-3);flex-shrink:0}
.vs-search-results{max-height:420px;overflow-y:auto;padding:8px}
.vs-search-result{display:flex;align-items:center;gap:12px;padding:11px 14px;border-radius:12px;cursor:pointer;transition:background .15s}
.vs-search-result:hover,.vs-search-result.focused{background:var(--surface-3)}
.vs-search-result-icon{font-size:16px;width:32px;height:32px;background:var(--surface-2);border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.vs-search-result-title{font-size:14px;font-weight:600;color:var(--text)}
.vs-search-result-sub{font-size:12px;color:var(--text-3);margin-top:1px}
.vs-search-hint{padding:14px 22px;display:flex;gap:16px;border-top:1px solid var(--border)}
.vs-search-hint-item{font-size:12px;color:var(--text-3);display:flex;align-items:center;gap:5px}
.vs-search-hint-kbd{padding:2px 6px;background:var(--surface-2);border-radius:4px;font-size:10px;font-weight:700;color:var(--text-3)}
.vs-search-section-label{font-size:11px;font-weight:700;color:var(--text-3);text-transform:uppercase;letter-spacing:.7px;padding:8px 14px 4px}
.vs-search-no-results{text-align:center;padding:32px;color:var(--text-3);font-size:14px}
/* ── SHARE LINK ── */
.vs-share-toggle{display:flex;align-items:center;gap:8px;padding:10px 14px;background:var(--surface-2);border-radius:10px;margin-bottom:10px}
.vs-share-toggle-label{font-size:13px;font-weight:600;color:var(--text-2);flex:1}
.vs-toggle-switch{position:relative;width:36px;height:20px;flex-shrink:0}
.vs-toggle-switch input{opacity:0;width:0;height:0;position:absolute}
.vs-toggle-slider{position:absolute;inset:0;background:var(--surface-3);border-radius:20px;cursor:pointer;transition:background .2s}
.vs-toggle-slider::before{content:'';position:absolute;width:14px;height:14px;left:3px;top:3px;background:white;border-radius:50%;transition:transform .2s}
.vs-toggle-switch input:checked + .vs-toggle-slider{background:var(--accent)}
.vs-toggle-switch input:checked + .vs-toggle-slider::before{transform:translateX(16px)}
.vs-share-link-box{display:flex;gap:8px;align-items:center;padding:10px 14px;background:var(--surface-2);border-radius:10px;border:1px solid var(--border)}
.vs-share-link-url{font-size:12px;color:var(--text-2);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:var(--font-mono)}
.vs-share-copy-btn{padding:5px 12px;background:var(--accent);color:var(--on-accent);border:none;border-radius:7px;font-size:11px;font-weight:700;cursor:pointer;font-family:var(--font-body);transition:background .18s;flex-shrink:0}
.vs-share-copy-btn:hover{background:var(--accent-hover)}
/* ── BATCH 2: STATUS BADGES ── */
.vs-status{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;letter-spacing:.3px;flex-shrink:0}
.vs-status.active{background:var(--success-tint);color:var(--success)}
.vs-status.in_progress{background:var(--warning-tint);color:var(--warning)}
.vs-status.completed{background:var(--info-tint);color:var(--info)}
.vs-status.on_hold{background:var(--danger-tint);color:var(--danger)}
.vs-status.archived{background:var(--surface-2);color:var(--text-2)}
/* ── BATCH 2: PIN ── */
.vs-pin-btn{background:none;border:none;cursor:pointer;font-size:16px;padding:4px 6px;border-radius:6px;transition:all .15s;opacity:.4;line-height:1}
.vs-pin-btn:hover{opacity:1;background:var(--accent-tint)}
.vs-pin-btn.pinned{opacity:1}
.vs-pinned-bar{margin-bottom:var(--space-6)}
.vs-pinned-label{font-size:11.5px;font-weight:600;color:var(--text-3);text-transform:uppercase;letter-spacing:.1em;margin-bottom:var(--space-3);display:flex;align-items:center;gap:6px}
.vs-pinned-row{display:grid;grid-template-columns:repeat(3,1fr);gap:var(--space-4)}
.vs-pinned-card{background:var(--surface);border:1px solid var(--border);border-top:2px solid var(--accent);border-radius:var(--radius);padding:var(--space-4);cursor:pointer;box-shadow:var(--shadow-xs);transition:transform var(--duration-fast) var(--ease-smooth-out),box-shadow var(--duration-fast) var(--ease-smooth-out),border-color var(--duration-fast) var(--ease-smooth-out)}
.vs-pinned-card:hover{box-shadow:var(--shadow-md);transform:translateY(var(--lift))}
.vs-pinned-card-title{font-size:14px;font-weight:600;color:var(--text);margin-bottom:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vs-pinned-card-tech{font-size:11px;color:var(--text-3);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
/* ── BATCH 2: DUE DATE ── */
.vs-due{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:600;padding:3px 9px;border-radius:20px}
.vs-due.ok{background:var(--info-tint);color:var(--info)}
.vs-due.soon{background:var(--warning-tint);color:var(--warning)}
.vs-due.overdue{background:var(--danger-tint);color:var(--danger)}
/* ── BATCH 2: TEMPLATES ── */
.vs-template-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px}
.vs-template-card{background:var(--surface-2);border:1.5px solid var(--border);border-radius:14px;padding:14px;cursor:pointer;transition:all .18s;text-align:left}
.vs-template-card:hover{border-color:var(--accent-soft);background:var(--surface-3)}
.vs-template-card-icon{font-size:22px;margin-bottom:6px}
.vs-template-card-name{font-size:13px;font-weight:700;color:var(--text);margin-bottom:2px}
.vs-template-card-desc{font-size:11px;color:var(--text-3)}
/* ── BATCH 3: SKELETONS ── */
@keyframes vsSkeleton{0%{opacity:.5}50%{opacity:1}100%{opacity:.5}}
.vs-skeleton{background:linear-gradient(90deg,var(--surface-2) 25%,var(--surface-3) 50%,var(--surface-2) 75%);background-size:200% 100%;animation:vsSkeleton 1.4s ease infinite;border-radius:8px}
.vs-skeleton-card{background:var(--surface);border-radius:18px;padding:20px;border:1px solid var(--border);margin-bottom:10px;display:flex;justify-content:space-between;align-items:center}
.vs-skeleton-line{height:14px;border-radius:6px;margin-bottom:8px}
.vs-skeleton-line.short{width:40%}
.vs-skeleton-line.med{width:65%}
.vs-skeleton-line.long{width:85%}
/* ── BATCH 3: EMPTY STATES ── */
.vs-empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;text-align:center}
.vs-empty-state-svg{margin-bottom:20px;opacity:.85}
.vs-empty-state-title{font-family:var(--font-display);font-size:20px;color:var(--text);margin-bottom:8px}
.vs-empty-state-sub{font-size:14px;color:var(--text-3);line-height:1.6;margin-bottom:20px;max-width:280px}
/* ── BATCH 3: ONBOARDING ── */
.vs-onboard-overlay{position:fixed;inset:0;background:var(--overlay);backdrop-filter:blur(4px);z-index:400;display:flex;align-items:center;justify-content:center;padding:20px;animation:vsFadeIn .3s ease}
.vs-onboard-card{background:var(--bg);border-radius:24px;padding:40px;width:100%;max-width:460px;box-shadow:var(--shadow-lg);animation:vsScaleIn .3s ease;text-align:center}
.vs-onboard-step-dots{display:flex;justify-content:center;gap:8px;margin-bottom:28px}
.vs-onboard-dot{width:8px;height:8px;border-radius:50%;background:var(--surface-3);transition:all .2s}
.vs-onboard-dot.active{background:var(--accent);width:24px;border-radius:4px}
.vs-onboard-icon{font-size:52px;margin-bottom:16px}
.vs-onboard-title{font-family:var(--font-display);font-size:24px;color:var(--text);margin-bottom:10px}
.vs-onboard-desc{font-size:14px;color:var(--text-2);line-height:1.7;margin-bottom:28px}
.vs-onboard-btn-row{display:flex;gap:10px;justify-content:center}
/* ── BATCH 3: SHORTCUTS HINT ── */
.vs-shortcuts-hint{position:fixed;bottom:20px;right:20px;background:var(--surface);border-radius:12px;padding:8px 14px;font-size:12px;color:var(--text-2);display:flex;align-items:center;gap:8px;z-index:200;animation:vsFadeIn .3s ease;border:1px solid var(--border)}
.vs-kbd{padding:2px 7px;background:var(--surface-3);border-radius:5px;font-size:10px;font-weight:700;color:var(--text);font-family:var(--font-mono)}
/* ── BATCH 4: ACTIVITY LOG ── */
.vs-activity-feed{display:flex;flex-direction:column;gap:2px}
.vs-activity-entry{display:flex;align-items:flex-start;gap:10px;padding:9px 12px;border-radius:10px;transition:background .15s}
.vs-activity-entry:hover{background:var(--surface-3)}
.vs-activity-avatar{width:28px;height:28px;border-radius:50%;background:var(--accent);color:var(--on-accent);font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-family:var(--font-display)}
.vs-activity-body{flex:1;min-width:0}
.vs-activity-text{font-size:13px;color:var(--text-2);line-height:1.5}
.vs-activity-text strong{color:var(--text);font-weight:600}
.vs-activity-time{font-size:11px;color:var(--text-3);margin-top:2px}
.vs-activity-icon{font-size:15px;flex-shrink:0;margin-top:1px}
/* ── BATCH 4: @MENTIONS ── */
.vs-mention{color:var(--accent);font-weight:700;background:var(--accent-tint);padding:1px 5px;border-radius:4px}
.vs-mention-dropdown{position:absolute;background:var(--bg);border:1px solid var(--border);border-radius:12px;box-shadow:var(--shadow-lg);z-index:300;overflow:hidden;min-width:200px;max-height:180px;overflow-y:auto}
.vs-mention-item{display:flex;align-items:center;gap:10px;padding:9px 14px;cursor:pointer;transition:background .15s;font-size:13px;color:var(--text)}
.vs-mention-item:hover,.vs-mention-item.focused{background:var(--surface-3)}
.vs-mention-av{width:28px;height:28px;border-radius:50%;background:var(--accent);color:var(--on-accent);font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.vs-comment-input-wrap{position:relative;flex:1}
/* ── BATCH 4: TEAMS ── */
.vs-team-card{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:18px;margin-bottom:12px;display:flex;align-items:center;gap:14px;cursor:pointer;transition:all .18s}
.vs-team-card:hover{border-color:var(--accent-soft);transform:translateY(-1px)}
.vs-team-avatar{width:44px;height:44px;border-radius:12px;background:linear-gradient(135deg,var(--accent),var(--accent));display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}
.vs-team-name{font-size:15px;font-weight:700;color:var(--text)}
.vs-team-members{font-size:12px;color:var(--text-3);margin-top:2px}
.vs-create-team-btn{width:100%;padding:14px;border:2px dashed var(--border);border-radius:16px;background:transparent;color:var(--text-3);font-family:var(--font-body);font-size:14px;cursor:pointer;transition:all .18s;margin-bottom:16px}
.vs-create-team-btn:hover{border-color:var(--accent);color:var(--accent);background:var(--accent-tint)}
/* ── TEAM MANAGEMENT PANEL ── */
.vs-team-layout{display:grid;grid-template-columns:320px 1fr;gap:18px}
@media(max-width:820px){.vs-team-layout{grid-template-columns:1fr}}
.vs-team-list-panel{display:flex;flex-direction:column;gap:10px}
.vs-team-card{background:var(--surface);border:1.5px solid var(--border);border-radius:16px;padding:16px;cursor:pointer;transition:all .18s}
.vs-team-card:hover{border-color:var(--accent-soft);transform:translateY(-1px)}
.vs-team-card.active{border-color:var(--accent);background:var(--surface)}
.vs-team-avatar{width:40px;height:40px;border-radius:12px;background:linear-gradient(135deg,var(--accent),var(--accent));display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
.vs-team-name{font-size:14px;font-weight:700;color:var(--text)}
.vs-team-members{font-size:12px;color:var(--text-3);margin-top:2px}
.vs-team-manage-panel{background:var(--surface);border:1px solid var(--border);border-radius:18px;padding:24px}
.vs-manage-title{font-family:var(--font-display);font-size:20px;color:var(--text);margin-bottom:4px}
.vs-manage-sub{font-size:13px;color:var(--text-3);margin-bottom:20px}
.vs-member-row{display:flex;align-items:center;gap:12px;padding:10px 12px;background:var(--surface-2);border-radius:12px;margin-bottom:8px}
.vs-member-av{width:34px;height:34px;border-radius:50%;background:var(--accent);color:var(--on-accent);font-size:13px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;overflow:hidden}
.vs-member-av img{width:100%;height:100%;object-fit:cover;border-radius:50%}
.vs-member-name{font-size:13px;font-weight:600;color:var(--text);flex:1}
.vs-member-email{font-size:11px;color:var(--text-3)}
.vs-owner-badge{padding:2px 8px;background:var(--accent);color:var(--on-accent);border-radius:20px;font-size:10px;font-weight:700;flex-shrink:0}
.vs-remove-btn{padding:4px 10px;background:var(--danger-tint);color:var(--danger);border:1px solid var(--danger-border);border-radius:8px;font-size:11px;font-weight:600;cursor:pointer;font-family:var(--font-body);transition:all .15s;flex-shrink:0}
.vs-remove-btn:hover{background:var(--danger-tint)}
.vs-invite-row{display:flex;gap:10px;margin-top:16px}
/* ── CERTIFICATES ── */
.vs-cert-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:16px}
.vs-cert-card{background:linear-gradient(135deg,var(--surface) 0%,var(--surface-2) 100%);border:1.5px solid var(--border);border-radius:18px;padding:22px;cursor:pointer;transition:all .2s;position:relative;overflow:hidden}
.vs-cert-card::before{content:"";position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,var(--accent),var(--accent))}
.vs-cert-card:hover{border-color:var(--accent);transform:translateY(-2px);box-shadow:var(--shadow-lg)}
.vs-cert-badge{font-size:40px;margin-bottom:12px}
.vs-cert-name{font-family:var(--font-display);font-size:16px;color:var(--text);margin-bottom:4px;font-weight:700}
.vs-cert-issuer{font-size:12px;color:var(--accent);font-weight:600;margin-bottom:6px}
.vs-cert-date{font-size:11px;color:var(--text-3)}
.vs-cert-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:10px}
.vs-cert-file-count{font-size:11px;font-weight:700;color:var(--accent);background:var(--accent-tint);padding:3px 9px;border-radius:20px;margin-top:8px;display:inline-block}
.vs-add-cert-btn{width:100%;padding:18px;border:2px dashed var(--accent-soft);border-radius:18px;background:transparent;color:var(--text-3);font-family:var(--font-body);font-size:14px;cursor:pointer;transition:all .18s;display:flex;align-items:center;justify-content:center;gap:10px}
.vs-add-cert-btn:hover{border-color:var(--accent);color:var(--accent);background:var(--accent-tint)}
/* ── SURVEY REMINDER BANNER ── */
.vs-survey-banner{display:flex;align-items:center;gap:14px;background:var(--accent-tint);border:1px solid var(--accent-soft);border-radius:16px;padding:16px 20px;margin-bottom:18px;position:relative;overflow:hidden;animation:vsSlideUp .4s ease both}
.vs-survey-banner::before{content:"";position:absolute;inset:0;background:radial-gradient(circle at 80% 50%,rgba(139,78,42,.06),transparent 60%);pointer-events:none}
.vs-survey-banner-icon{font-size:28px;flex-shrink:0}
.vs-survey-banner-text{flex:1;min-width:0}
.vs-survey-banner-title{font-size:14px;font-weight:700;color:var(--text);margin-bottom:2px}
.vs-survey-banner-sub{font-size:12px;color:var(--text-2)}
.vs-survey-banner-btn{padding:8px 18px;background:var(--accent);color:var(--on-accent);border:none;border-radius:10px;font-family:var(--font-body);font-size:12px;font-weight:700;cursor:pointer;flex-shrink:0;transition:background .15s}
.vs-survey-banner-btn:hover{background:var(--accent-hover)}
.vs-survey-banner-close{background:none;border:none;color:var(--text-3);font-size:18px;cursor:pointer;padding:4px;flex-shrink:0;line-height:1;transition:color .15s}
.vs-survey-banner-close:hover{color:var(--text)}
/* ── FOLDER CARDS ── */
.vs-folder-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:var(--space-5)}
.vs-folder-card{min-width:0;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:var(--space-5);cursor:pointer;box-shadow:var(--shadow-xs);transition:transform var(--duration-fast) var(--ease-smooth-out),box-shadow var(--duration-fast) var(--ease-smooth-out),border-color var(--duration-fast) var(--ease-smooth-out);position:relative;animation:vsSlideUp .3s ease backwards}
.vs-folder-card:hover{border-color:var(--border-strong);transform:translateY(var(--lift));box-shadow:var(--shadow-md)}
.vs-folder-card:active,.vs-pinned-card:active,.vs-project-card:active{transform:none;box-shadow:var(--shadow-sm);transition-duration:var(--duration-quick)}
@media (hover:none){.vs-folder-card:hover,.vs-pinned-card:hover,.vs-project-card:hover{transform:none}}
.vs-folder-icon{font-size:32px;margin-bottom:var(--space-4);line-height:1}
.vs-folder-name{font-family:var(--font-display);font-size:18px;font-weight:500;letter-spacing:-.01em;color:var(--text);margin-bottom:var(--space-2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vs-folder-desc{font-size:13px;color:var(--text-3);margin-bottom:var(--space-4);overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;line-height:1.5}
.vs-folder-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.vs-folder-file-count{font-size:11.5px;font-weight:600;color:var(--accent);background:var(--accent-tint);padding:3px 10px;border-radius:var(--radius-pill)}
.vs-folder-date{font-size:11px;color:var(--text-3)}
.vs-folder-pin{position:absolute;top:12px;right:12px;font-size:13px;opacity:.4;cursor:pointer;transition:opacity .15s;background:none;border:none;padding:2px}
.vs-folder-pin:hover,.vs-folder-pin.pinned{opacity:1}
.vs-folder-actions{display:flex;gap:6px;margin-top:var(--space-4);opacity:0;transition:opacity .18s}
.vs-folder-card:focus-within .vs-folder-actions{opacity:1}
.vs-folder-card:hover .vs-folder-actions{opacity:1}
/* ── FOLDER FILES LIST (in drawer) ── */
.vs-file-list{display:flex;flex-direction:column;gap:6px}
.vs-file-row{display:flex;align-items:center;gap:10px;padding:10px 12px;background:var(--surface-2);border-radius:12px;border:1px solid var(--border);transition:background .15s}
.vs-file-row:hover{background:var(--surface-3)}
.vs-file-row-icon{font-size:20px;flex-shrink:0;width:32px;height:32px;background:var(--surface);border-radius:8px;display:flex;align-items:center;justify-content:center}
.vs-file-row-name{font-size:13px;font-weight:600;color:var(--text);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vs-file-row-type{font-size:10px;font-weight:700;color:var(--text-3);text-transform:uppercase;letter-spacing:.5px}
.vs-file-row-actions{display:flex;gap:6px;flex-shrink:0}
.vs-file-action-btn{padding:4px 10px;border-radius:7px;font-size:11px;font-weight:600;cursor:pointer;font-family:var(--font-body);border:none;transition:all .15s}
.vs-file-action-btn.view{background:var(--success-tint);color:var(--success)}
.vs-file-action-btn.view:hover{background:var(--success-tint)}
.vs-file-action-btn.dl{background:var(--info-tint);color:var(--info)}
.vs-file-action-btn.dl:hover{background:var(--info-tint)}
.vs-file-action-btn.del{background:var(--danger-tint);color:var(--danger)}
.vs-file-action-btn.del:hover{background:var(--danger-tint)}
/* UPLOAD DROP ZONE in drawer */
.vs-drop-zone{border:2px dashed var(--accent-soft);border-radius:14px;padding:20px;text-align:center;cursor:pointer;transition:all .2s;margin-top:12px;position:relative}
.vs-drop-zone:hover{border-color:var(--accent);background:var(--accent-tint)}
.vs-drop-zone input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}
.vs-drop-zone-icon{font-size:24px;margin-bottom:6px}
.vs-drop-zone-text{font-size:13px;color:var(--text-3);font-weight:500}
.vs-drop-zone-hint{font-size:11px;color:var(--text-3);margin-top:3px}
/* ── DASHBOARD TABS ── */
.vs-dash-tabs{display:flex;gap:4px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius);padding:4px;margin-bottom:var(--space-5);width:fit-content}
.vs-dash-tab{padding:8px 20px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;border:none;font-family:var(--font-body);background:transparent;color:var(--text-3);transition:all .18s}
.vs-dash-tab.active{background:var(--surface);color:var(--text);box-shadow:var(--shadow-sm)}
.vs-dash-tab:not(.active):hover{color:var(--text)}
.vs-dash-tab-count{margin-left:6px;background:var(--accent-soft);color:var(--accent);border-radius:20px;padding:1px 7px;font-size:10px;font-weight:700}
.vs-dash-tab.active .vs-dash-tab-count{background:var(--accent);color:var(--on-accent)}
/* ── SHARED FOLDER CARD ── */
.vs-folder-card.shared{border-color:var(--success-border);background:var(--success-tint)}
.vs-folder-card.shared:hover{border-color:var(--success-border)}
.vs-shared-by{font-size:11px;color:var(--success);font-weight:600;display:flex;align-items:center;gap:4px;margin-top:6px}
.vs-role-chip{padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
.vs-role-chip.viewer{background:var(--info-tint);color:var(--info)}
.vs-role-chip.editor{background:var(--warning-tint);color:var(--warning)}
.vs-role-chip.admin{background:var(--danger-tint);color:var(--danger)}
/* ── AI SUMMARISER ── */
.vs-ai-overlay{position:fixed;inset:0;background:var(--overlay);backdrop-filter:blur(6px);z-index:500;display:flex;align-items:center;justify-content:center;padding:20px;animation:vsFadeIn .2s ease}
.vs-ai-modal{background:var(--bg);border-radius:24px;width:100%;max-width:680px;max-height:90vh;display:flex;flex-direction:column;box-shadow:var(--shadow-lg);animation:vsScaleIn .22s ease;overflow:hidden}
.vs-ai-header{padding:22px 28px 18px;border-bottom:1px solid var(--border);display:flex;justify-content:space-between;align-items:flex-start;flex-shrink:0}
.vs-ai-title{font-family:var(--font-display);font-size:20px;color:var(--text)}
.vs-ai-file-name{font-size:12px;color:var(--text-3);margin-top:3px}
.vs-ai-body{flex:1;overflow-y:auto;padding:24px 28px}
.vs-ai-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 0;gap:16px}
.vs-ai-spinner{width:40px;height:40px;border:3px solid var(--border);border-top-color:var(--accent);border-radius:50%;animation:vsAiSpin 0.8s linear infinite}
@keyframes vsAiSpin{to{transform:rotate(360deg)}}
.vs-ai-loading-text{font-size:14px;color:var(--text-3);font-style:italic}
.vs-ai-section{margin-bottom:22px}
.vs-ai-section-title{font-size:11px;font-weight:700;color:var(--accent);text-transform:uppercase;letter-spacing:1.2px;margin-bottom:10px;display:flex;align-items:center;gap:6px}
.vs-ai-summary-text{font-size:14px;color:var(--text-2);line-height:1.8;background:var(--surface-2);border-radius:12px;padding:16px}
.vs-ai-point{display:flex;align-items:flex-start;gap:10px;padding:8px 0;border-bottom:1px solid var(--border)}
.vs-ai-point:last-child{border-bottom:none}
.vs-ai-point-dot{width:6px;height:6px;border-radius:50%;background:var(--accent);flex-shrink:0;margin-top:6px}
.vs-ai-point-text{font-size:13px;color:var(--text-2);line-height:1.6}
.vs-ai-chip{display:inline-flex;align-items:center;padding:5px 12px;background:var(--accent-tint);border:1px solid var(--accent-soft);border-radius:20px;font-size:12px;color:var(--text-2);font-weight:500;margin:3px}
.vs-ai-btn{padding:6px 14px;background:var(--accent);color:var(--on-accent);border:none;border-radius:10px;font-size:12px;font-weight:600;cursor:pointer;font-family:var(--font-body);display:inline-flex;align-items:center;gap:5px;transition:background .18s}
.vs-ai-btn:hover{background:var(--accent-hover)}
.vs-file-action-btn.ai{background:#f3e8ff;color:#6b21a8}
.vs-file-action-btn.ai:hover{background:#e9d5ff}
/* ── ANALYTICS ── */
.vs-analytics-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px}
@media(max-width:820px){.vs-analytics-grid{grid-template-columns:1fr}}

/* ══════════════════════════════════════
   MOBILE RESPONSIVE — 768px and below
══════════════════════════════════════ */
@media(max-width:768px){
  .vs-shell{flex-direction:column;background:var(--bg)}
  .vs-sidebar{width:100%;min-height:unset;height:56px;flex-direction:row;align-items:center;padding:0 16px;position:fixed;top:0;left:0;right:0;z-index:200;border-radius:0;box-shadow:var(--shadow-md)}
  .vs-sidebar .vs-nav{display:none}
  .vs-sidebar .vs-sidebar-footer{display:none}
  .vs-sidebar .vs-notif-bell{margin-left:auto}
  .vs-main{border-radius:0;padding:72px 14px 90px;min-height:100vh}
  .vs-bottom-nav{display:flex !important;position:fixed;bottom:0;left:0;right:0;background:var(--surface);padding:6px 0 max(6px,env(safe-area-inset-bottom));z-index:200;box-shadow:var(--shadow-md);justify-content:space-around;align-items:center}
  .vs-bottom-nav-btn{display:flex;flex-direction:column;align-items:center;gap:2px;padding:4px 8px;border-radius:8px;border:none;background:transparent;color:var(--text-2);font-family:var(--font-body);font-size:9px;font-weight:600;cursor:pointer;transition:all .15s;min-width:44px;text-transform:uppercase;letter-spacing:.3px}
  .vs-bottom-nav-btn.active{color:var(--text);background:var(--surface-3)}
  .vs-bottom-nav-btn .bn-icon{font-size:17px;line-height:1}
  .vs-topbar{flex-direction:column;gap:10px;margin-bottom:14px}
  .vs-topbar-actions{width:100%;justify-content:flex-end;flex-wrap:wrap;gap:6px}
  .vs-page-title{font-size:20px}
  .vs-stats-bar{grid-template-columns:1fr 1fr;gap:8px}
  .vs-stat-widget{padding:12px}
  .vs-stat-widget-num{font-size:20px}
  .vs-dash-tabs{width:100%}
  .vs-dash-tab{flex:1;text-align:center;padding:7px 6px;font-size:11px}
  .vs-folder-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:var(--space-3)}
  .vs-folder-card{padding:12px}
  .vs-folder-icon{font-size:26px;margin-bottom:6px}
  .vs-folder-name{font-size:13px}
  .vs-folder-actions{opacity:1}
  .vs-pinned-row{flex-wrap:nowrap;overflow-x:auto;padding-bottom:6px}
  .vs-filter-row{flex-direction:column}
  .vs-pagination{gap:6px}
  .vs-drawer-overlay{align-items:flex-end;padding:0}
  .vs-drawer{width:100% !important;max-width:100% !important;height:90vh;border-radius:20px 20px 0 0;padding:18px 14px}
  .vs-drawer-tabs{gap:2px;overflow-x:auto;flex-wrap:nowrap}
  .vs-drawer-tab{padding:7px 10px;font-size:11px;white-space:nowrap;flex-shrink:0}
  .vs-drawer-footer{gap:6px}
  .vs-drawer-footer button{flex:1;font-size:11px;padding:9px 6px}
  .vs-file-row{flex-wrap:wrap;gap:6px}
  .vs-file-row-actions{width:100%;justify-content:flex-end;gap:4px}
  .vs-modal-overlay{padding:0;align-items:flex-end}
  .vs-modal{width:100%;max-width:100%;border-radius:20px 20px 0 0;max-height:94vh;overflow-y:auto;padding:22px 16px 36px}
  .vs-modal-btn-row{flex-direction:column}
  .vs-modal-btn-row button{width:100%}
  .vs-editor-modal{width:100%;max-width:100%;border-radius:20px 20px 0 0;height:94vh}
  .vs-ai-modal{max-width:100%;border-radius:20px 20px 0 0}
  .vs-contribution-grid{grid-template-columns:repeat(26,1fr)}
  .vs-search-box{width:calc(100vw - 32px);top:64px}
  .vs-onboard-modal{width:calc(100vw - 32px);padding:22px 18px}
  .vs-shortcuts-panel{width:calc(100vw - 32px);right:16px}
  .vs-lang-grid{grid-template-columns:1fr 1fr}
  .vs-settings-card{padding:16px}
  .vs-bin-card{flex-direction:column;align-items:flex-start;gap:8px}
  .vs-bin-actions{width:100%;justify-content:flex-end}
  .vs-team-manage-panel{padding:16px}
  .vs-member-row{flex-wrap:wrap;gap:6px}
}
.vs-chart-card{background:var(--surface);border-radius:18px;padding:22px;border:1px solid var(--border)}
.vs-chart-title{font-family:var(--font-display);font-size:17px;color:var(--text);margin-bottom:4px}
.vs-chart-sub{font-size:12px;color:var(--text-3);margin-bottom:16px}
.vs-contribution-grid{display:grid;grid-template-columns:repeat(52,1fr);gap:2px;overflow-x:auto}
.vs-contrib-cell{width:11px;height:11px;border-radius:2px;background:var(--surface-2);transition:background .15s}
.vs-contrib-cell:hover{opacity:.8}
.vs-contrib-cell.l1{background:var(--accent-soft)}
.vs-contrib-cell.l2{background:var(--accent)}
.vs-contrib-cell.l3{background:var(--accent)}
.vs-contrib-cell.l4{background:var(--accent)}
.vs-bar-chart{display:flex;align-items:flex-end;gap:6px;height:100px;padding-bottom:20px;position:relative}
.vs-bar-wrap{display:flex;flex-direction:column;align-items:center;flex:1;gap:4px;height:100%}
.vs-bar{width:100%;border-radius:4px 4px 0 0;background:var(--accent);transition:height .4s ease;min-height:2px}
.vs-bar-label{font-size:9px;color:var(--text-3);font-weight:600;position:absolute;bottom:0}
.vs-donut-wrap{display:flex;align-items:center;gap:20px}
.vs-donut-legend{display:flex;flex-direction:column;gap:8px}
.vs-donut-legend-item{display:flex;align-items:center;gap:8px;font-size:12px;color:var(--text-2)}
.vs-donut-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}`;

/* ── NAV CONFIG ── */
const NAV = [
  {key:"projects",      label:"Dashboard",    icon:"⊞"},
  {key:"collab",        label:"Collaborate",  icon:"🤝"},
  {key:"analytics",     label:"Analytics",    icon:"📊"},
  {key:"certificates",  label:"Certificates", icon:"🏆"},
  {key:"teams",         label:"Teams",        icon:"👥"},
  {key:"survey",        label:"Survey",       icon:"📝"},
  {key:"bin",           label:"Recycle Bin",  icon:"🗑️"},
  {key:"settings",      label:"Settings",     icon:"⚙️"},
  {key:"profile",       label:"Profile",      icon:"◉"},
];

/* ══════════════════════════════════
   COMPONENT
══════════════════════════════════ */
function Dashboard() {
  const [activeNav, setActiveNav]   = useState("projects");

  // Projects
  const [projects, setProjects]     = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  const [dashTab, setDashTab]       = useState("mine"); // "mine" | "shared"
  const [search, setSearch]         = useState("");
  const [filterTag, setFilterTag]   = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);

  // Bin
  const [binProjects, setBinProjects] = useState([]);

  // Drawer
  const [drawer, setDrawer]         = useState(null);
  const [drawerTab, setDrawerTab]   = useState("details"); // "details" | "comments" | "history"
  const [drawerComments, setDrawerComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [versions, setVersions]     = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);

  // Create/Edit modal
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [fTitle, setFTitle]         = useState("");
  const [fDesc, setFDesc]           = useState("");
  const [fTags, setFTags]           = useState("");
  const [fFiles, setFFiles]         = useState([]); // multiple files
  const [fChangeNote, setFChangeNote] = useState("");

  // Editor
  const [editorProj, setEditorProj] = useState(null);
  const [editorText, setEditorText] = useState("");
  const [editorSaving, setEditorSaving] = useState(false);

  // Collab
  const [collabTab, setCollabTab]   = useState("send");
  const [collabQ, setCollabQ]       = useState("");
  const [collabResults, setCollabResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [invEmail, setInvEmail]     = useState("");
  const [invProject, setInvProject] = useState("");
  const [invRole, setInvRole]       = useState("viewer");
  const [sentInvites, setSentInvites]   = useState([]);
  const [recvInvites, setRecvInvites]   = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);

  // Settings
  const [selLang, setSelLang]       = useState(localStorage.getItem("language") || "english");

  // ── BATCH 1: Notifications ──
  const [notifications, setNotifications]   = useState([]);
  const [unreadCount, setUnreadCount]       = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  // ── BATCH 1: Global Search ──
  const [showSearch, setShowSearch]         = useState(false);
  const [searchQuery, setSearchQuery]       = useState("");
  const [searchResults, setSearchResults]   = useState([]);
  const [searchFocusIdx, setSearchFocusIdx] = useState(0);
  const searchInputRef = useRef(null);

  // ── BATCH 1: Stats ──
  const [dashStats, setDashStats]           = useState(null);

  // ── BATCH 2: Status / Due / Pin / Templates ──
  const [fStatus, setFStatus]               = useState("active");
  const [fDueDate, setFDueDate]             = useState("");
  const [showTemplates, setShowTemplates]   = useState(false);

  // ── BATCH 3: Onboarding / Skeletons / Shortcuts ──
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardStep, setOnboardStep]       = useState(0);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [showShortcuts, setShowShortcuts]   = useState(false);

  // ── AI SUMMARISER ──
  const [aiModal, setAiModal]               = useState(null); // {file, projectTitle}
  const [aiResult, setAiResult]             = useState(null);
  const [aiLoading, setAiLoading]           = useState(false);

  // ── ANALYTICS ──
  const [analyticsData, setAnalyticsData]   = useState(null);

  // ── SURVEY REMINDER BANNER ──
  const [showSurveyBanner, setShowSurveyBanner] = useState(() => {
    const done      = localStorage.getItem("vs_survey_done");
    const dismissed = localStorage.getItem("vs_survey_dismissed");
    const lastShown = localStorage.getItem("vs_survey_last_shown");
    if (done) return false;
    if (!dismissed) return true;
    // Re-show every 3 days even if dismissed
    if (lastShown) {
      const daysSince = (Date.now() - parseInt(lastShown)) / 86400000;
      return daysSince >= 3;
    }
    return false;
  });

  const dismissSurveyBanner = () => {
    setShowSurveyBanner(false);
    localStorage.setItem("vs_survey_dismissed", "1");
    localStorage.setItem("vs_survey_last_shown", Date.now().toString());
  };
  const [activityLog, setActivityLog]       = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [mentionQuery, setMentionQuery]     = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionIdx, setMentionIdx]         = useState(0);
  const [showMentions, setShowMentions]     = useState(false);
  const [teams, setTeams]                   = useState([]);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName]       = useState("");
  const [selectedTeam, setSelectedTeam]     = useState(null);
  const [teamInviteEmail, setTeamInviteEmail] = useState("");
  const [invitingToTeam, setInvitingToTeam] = useState(false);

  // ── CERTIFICATES ──
  const [certificates, setCertificates]     = useState([]);
  const [showCertModal, setShowCertModal]   = useState(false);
  const [certName, setCertName]             = useState("");
  const [certIssuer, setCertIssuer]         = useState("");
  const [certDate, setCertDate]             = useState("");
  const [certTags, setCertTags]             = useState("");
  const [certFiles, setCertFiles]           = useState([]);
  const commentInputRef = useRef(null);

  const user     = JSON.parse(localStorage.getItem("user") || "null");
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  /* inject styles */
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = S;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  /* ── PROJECTS ── */
  const fetchProjects = useCallback(async (page, s, tech) => {
    const pg = page || 1;
    const sv = s !== undefined ? s : search;
    const tv = tech !== undefined ? tech : (filterTag === "All" ? "" : filterTag);
    setLoadingProjects(true);
    try {
      const url = "/projects?page=" + pg + "&limit=10&search=" + encodeURIComponent(sv) + "&tech=" + encodeURIComponent(tv);
      const res = await API.get(url);
      setProjects(res.data.projects||[]);
      setCurrentPage(res.data.currentPage);
      setTotalPages(res.data.totalPages);
      // Show onboarding on first load if no projects
      if (pg === 1 && !sv && !tv && res.data.totalProjects === 0) {
        const seen = localStorage.getItem("vs_onboarded");
        if (!seen) setShowOnboarding(true);
      }
    } catch { toast.error("Failed to fetch projects"); }
    finally { setLoadingProjects(false); }
  }, []);

  useEffect(() => { fetchProjects(); fetchSharedProjects(); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchProjects(1, search), 450);
    return () => clearTimeout(t);
  }, [search, filterTag]);

  const fetchSharedProjects = async () => {
    try {
      const res = await API.get("/collaborations/shared-projects");
      setSharedProjects(res.data.sharedProjects || []);
    } catch { setSharedProjects([]); }
  };

  /* ── BIN ── */
  const fetchBin = async () => {
    try {
      const res = await API.get("/projects/bin");
      setBinProjects(res.data.projects||[]);
    } catch { toast.error("Failed to fetch bin"); }
  };
  useEffect(() => { if (activeNav==="bin") fetchBin(); }, [activeNav]);

  /* ── BATCH 1: NOTIFICATIONS ── */
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await API.get("/notifications");
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch {}
  }, []);

  useEffect(() => { fetchNotifications(); }, []);

  // Poll for new notifications every 30s
  useEffect(() => {
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markNotifRead = async (id) => {
    try {
      await API.patch("/notifications/" + (id) + "/read");
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await API.patch("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const clearAllNotifs = async () => {
    try {
      await API.delete("/notifications");
      setNotifications([]);
      setUnreadCount(0);
    } catch {}
  };

  const NOTIF_ICONS = {
    collab_invite:   "🤝",
    invite_accepted: "✅",
    invite_declined: "❌",
    comment_added:   "💬",
    project_shared:  "🔗",
    system:          "📢",
  };

  const timeAgo = (date) => {
    const diff = Date.now() - new Date(date);
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return "just now";
    if (mins < 60) return (mins) + "m ago";
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return (hrs) + "h ago";
    return (Math.floor(hrs / 24)) + "d ago";
  };

  /* ── BATCH 1: STATS ── */
  const fetchDashStats = useCallback(async () => {
    try {
      const res = await API.get("/users/stats");
      setDashStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchDashStats(); }, []);

  /* ── BATCH 1: GLOBAL SEARCH ── */
  // Keyboard shortcut: "/" opens search
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement.tagName;
      const typing = ["INPUT","TEXTAREA","SELECT"].includes(tag);
      if (e.key === "/" && !typing) { e.preventDefault(); setShowSearch(true); }
      if (e.key === "Escape") { setShowSearch(false); setShowNotifPanel(false); setShowOnboarding(false); }
      if (e.key === "n" && !typing && !e.metaKey && !e.ctrlKey) { e.preventDefault(); openCreate(); }
      if (e.key === "?" && !typing) { setShowShortcuts(p => !p); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Focus input when search opens
  useEffect(() => {
    if (showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
      setSearchQuery("");
      setSearchResults([]);
      setSearchFocusIdx(0);
    }
  }, [showSearch]);

  // Search as user types
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const q = encodeURIComponent(searchQuery);
        const res = await API.get("/projects?search=" + (q) + "&limit=20");
        setSearchResults(res.data.projects || []);
        setSearchFocusIdx(0);
      } catch {}
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Keyboard nav inside search
  const handleSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSearchFocusIdx(i => Math.min(i + 1, searchResults.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setSearchFocusIdx(i => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && searchResults[searchFocusIdx]) {
      openDrawer(searchResults[searchFocusIdx]);
      setShowSearch(false);
    }
  };

  /* ── BATCH 1: SHARE LINKS ── */
  const toggleShare = async (projectId, enabled) => {
    try {
      const res = await API.patch("/projects/" + (projectId) + "/share", { enabled });
      toast.success(enabled ? "Share link enabled! 🔗" : "Share link disabled");
      fetchProjects();
      // update drawer if open
      if (drawer?._id === projectId) setDrawer(prev => ({ ...prev, shareEnabled: res.data.shareEnabled, shareId: res.data.shareId }));
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
  };

  const copyShareLink = (shareId) => {
    const url = (window.location.origin) + "/share/" + (shareId);
    navigator.clipboard.writeText(url);
    toast.success("Link copied! 📋");
  };

  /* ── COLLAB ── */
  useEffect(() => {
    if (activeNav==="collab") { fetchSentInvites(); fetchReceivedInvites(); }
  }, [activeNav]);

  const fetchSentInvites = async () => {
    try { const r = await API.get("/collaborations/sent"); setSentInvites(r.data.invites||[]); } catch {}
  };
  const fetchReceivedInvites = async () => {
    try { const r = await API.get("/collaborations/received"); setRecvInvites(r.data.invites||[]); } catch {}
  };

  /* ── USER SEARCH ── */
  useEffect(() => {
    if (collabQ.trim().length < 2) { setCollabResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const q = encodeURIComponent(collabQ.trim());
        const r = await API.get("/users/search?q=" + (q));
        setCollabResults(r.data.users||[]);
      } catch { toast.error("Search failed"); }
      finally { setSearchingUsers(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [collabQ]);

  /* ── COMMENTS ── */
  const fetchComments = async (projectId) => {
    try {
      const r = await API.get("/projects/" + (projectId) + "/comments");
      setDrawerComments(r.data.comments||[]);
    } catch { setDrawerComments([]); }
  };

  const openDrawer = (p) => {
    setDrawer(p);
    setDrawerTab("details");
    setNewComment("");
    setVersions([]);
    setActivityLog([]);
    fetchComments(p._id);
    fetchActivity(p._id);
  };

  const fetchVersions = async (projectId) => {
    setLoadingVersions(true);
    try {
      const r = await API.get("/projects/" + (projectId) + "/versions");
      setVersions(r.data.versions || []);
    } catch { setVersions([]); }
    finally { setLoadingVersions(false); }
  };

  const restoreVersion = async (projectId, versionNumber) => {
    if (!window.confirm("Restore to version " + (versionNumber) + "? Current state will be saved as a new version.")) return;
    setRestoringVersion(true);
    try {
      await API.post("/projects/" + (projectId) + "/restore-version/" + (versionNumber));
      toast.success("Restored to v" + (versionNumber) + " ✅");
      fetchProjects();
      fetchVersions(projectId);
      // refresh drawer with updated project
      const res = await API.get("/projects/" + (projectId));
      setDrawer(res.data.project);
    } catch (e) { toast.error(e.response?.data?.message || "Restore failed"); }
    finally { setRestoringVersion(false); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    setAddingComment(true);
    try {
      await API.post("/projects/" + (drawer._id) + "/comments", { text: newComment });
      setNewComment("");
      fetchComments(drawer._id);
      toast.success("Comment added");
    } catch { toast.error("Failed to add comment"); }
    finally { setAddingComment(false); }
  };

  /* ── CRUD ── */
  const resetForm = () => {
    setFTitle(""); setFDesc(""); setFTags(""); setFFiles([]);
    setFChangeNote(""); setFStatus("active"); setFDueDate("");
  };
  const openCreate = () => { resetForm(); setEditing(null); setShowModal(true); };
  const openEdit   = (p) => {
    setEditing(p); setFTitle(p.title); setFDesc(p.description||"");
    setFFiles([]); setFTags((p.tags||[]).join(", "));
    setFStatus(p.status||"active");
    setFDueDate(p.dueDate ? new Date(p.dueDate).toISOString().split("T")[0] : "");
    setShowModal(true);
  };

  const saveProject = async () => {
    if (!fTitle) { toast.error("Folder name is required"); return; }
    setLoading(true);
    const tagsArr = fTags.split(",").map(t=>t.trim()).filter(Boolean);
    try {
      if (editing) {
        // Edit = update details only (files managed separately in drawer)
        const body = new FormData();
        body.append("title", fTitle);
        body.append("description", fDesc);
        body.append("tags", JSON.stringify(tagsArr));
        body.append("status", fStatus);
        if (fDueDate) body.append("dueDate", fDueDate);
        if (fChangeNote.trim()) body.append("changeNote", fChangeNote);
        await API.put("/projects/" + editing._id, body);
        toast.success("Folder updated!");
      } else {
        // Create = folder + optional initial files
        const fd = new FormData();
        fd.append("title", fTitle);
        fd.append("description", fDesc);
        fd.append("tags", JSON.stringify(tagsArr));
        fd.append("status", fStatus);
        if (fDueDate) fd.append("dueDate", fDueDate);
        fFiles.forEach(f => fd.append("files", f));
        await API.post("/projects", fd);
        toast.success("Folder created! 📁");
      }
      setShowModal(false); resetForm(); fetchProjects();
    } catch (e) { toast.error(e.response?.data?.message||"Save failed"); }
    finally { setLoading(false); }
  };

  /* ── UPLOAD MORE FILES INTO EXISTING FOLDER ── */
  const uploadFilesToFolder = async (projectId, files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append("files", f));
    try {
      const res = await API.post("/projects/" + projectId + "/files", fd);
      toast.success(files.length + " file(s) uploaded ✅");
      setDrawer(res.data.project);
      fetchProjects();
    } catch (e) { toast.error(e.response?.data?.message || "Upload failed"); }
  };

  /* ── DELETE ONE FILE FROM FOLDER ── */
  const deleteFileFromFolder = async (projectId, fileId, fileName) => {
    if (!window.confirm("Delete \"" + fileName + "\"?")) return;
    try {
      const res = await API.delete("/projects/" + projectId + "/files/" + fileId);
      toast.success("File deleted");
      setDrawer(res.data.project);
      fetchProjects();
    } catch { toast.error("Failed to delete"); }
  };

  /* ── PIN ── */
  const togglePin = async (e, id, pinned) => {
    e.stopPropagation();
    try {
      await API.patch("/projects/" + id + "/pin", { pinned: !pinned });
      fetchProjects();
      toast.success(!pinned ? "📌 Pinned!" : "Unpinned");
    } catch { toast.error("Failed"); }
  };

  const softDelete = async (id) => {
    if (!window.confirm("Move to Recycle Bin?")) return;
    try { await API.patch("/projects/" + (id) + "/soft-delete"); toast.success("Moved to bin 🗑️"); fetchProjects(); }
    catch { toast.error("Delete failed"); }
  };

  const restoreProject = async (id) => {
    try { await API.patch("/projects/" + (id) + "/restore"); toast.success("Restored ✅"); fetchBin(); }
    catch { toast.error("Restore failed"); }
  };

  const permanentDelete = async (id) => {
    if (!window.confirm("Permanently delete? Cannot be undone.")) return;
    try { await API.delete("/projects/" + (id) + "/permanent"); toast.success("Permanently deleted"); fetchBin(); }
    catch { toast.error("Failed"); }
  };

  const downloadFile = async (url, name) => {
    try {
      const res  = await fetch(url);
      const blob = await res.blob();
      const a    = document.createElement("a");
      a.href     = URL.createObjectURL(blob);
      a.download = name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch {
      // Fallback
      const a = document.createElement("a");
      a.href = url; a.download = name || "download"; a.target = "_blank"; a.click();
    }
  };

  /* ── INLINE EDITOR ── */
  const [editorLoading, setEditorLoading] = useState(false);

  const openEditor = async (p) => {
    const ft = getFileType(p.file.originalName||"");
    if (ft==="img") { toast.info("Images can't be edited"); return; }
    setEditorProj(p);
    setEditorText("");
    setEditorLoading(true);
    try {
      const res = await API.post("/ai/fetch-text", {
        fileUrl: p.file.url,
        fileName: p.file.originalName,
      });
      setEditorText(res.data.content || "");
      if (!res.data.content) toast.warn("File appears empty");
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      toast.error("Load failed: " + msg);
      setEditorText("");
    } finally {
      setEditorLoading(false);
    }
  };

  const saveEditor = async () => {
    if (!editorProj) return;
    setEditorSaving(true);
    try {
      const blob = new Blob([editorText], { type: "text/plain" });
      const f    = new File([blob], editorProj.file.originalName, { type: "text/plain" });
      const fd   = new FormData();
      fd.append("files", f);
      if (editorProj.file._id) {
        try { await API.delete("/projects/" + editorProj._id + "/files/" + editorProj.file._id); } catch {}
      }
      const res = await API.post("/projects/" + editorProj._id + "/files", fd);
      toast.success("File saved ✅");
      // Update drawer if open
      if (drawer && drawer._id === editorProj._id) setDrawer(res.data.project);
      setEditorProj(null);
      fetchProjects();
    } catch { toast.error("Save failed"); }
    finally { setEditorSaving(false); }
  };

  /* ── COLLAB ── */
  const sendInvite = async () => {
    if (!invEmail) { toast.error("Email required"); return; }
    setSendingInvite(true);
    try {
      const r = await API.post("/collaborations/invite", { email:invEmail, projectId:invProject||undefined, role:invRole });
      toast.success("Invite sent to " + (invEmail) + " ✅");
      setSentInvites(prev=>[r.data.collaboration,...prev]);
      setInvEmail(""); setInvProject("");
    } catch(e) { toast.error(e.response?.data?.message||"Invite failed"); }
    finally { setSendingInvite(false); }
  };

  const respondInvite = async (id, action) => {
    try {
      await API.patch("/collaborations/" + (id) + "/" + (action));
      toast.success(action==="accept"?"Accepted! 🎉":"Declined");
      setRecvInvites(prev=>prev.map(i=>i._id===id?{...i,status:action==="accept"?"accepted":"declined"}:i));
    } catch(e) { toast.error(e.response?.data?.message||"Action failed"); }
  };

  /* ── SETTINGS ── */
  const applyLang = (code) => {
    setSelLang(code); i18n.changeLanguage(code); localStorage.setItem("language",code);
    toast.success("Language updated ✅");
  };

  /* ── LOGOUT ── */
  const logout = () => {
    if (!window.confirm(t("logoutConfirm"))) return;
    localStorage.clear(); window.location.href="/";
  };

  const initials = (n="") => n.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)||"?";
  const pendingCount   = recvInvites.filter(i=>i.status==="pending").length;
  const binCount       = binProjects.length;
  const pinnedProjects = projects.filter(p => p.pinned);

  const STATUS_LABELS = {active:"Active",in_progress:"In Progress",completed:"Completed",on_hold:"On Hold",archived:"Archived"};
  const STATUS_ICONS  = {active:"🟢",in_progress:"🟡",completed:"🔵",on_hold:"🔴",archived:"⬜"};

  const getDueLabel = (dueDate) => {
    if (!dueDate) return null;
    const days = Math.ceil((new Date(dueDate) - Date.now()) / 86400000);
    if (days < 0)  return { label: "Overdue by " + Math.abs(days) + "d", cls: "overdue" };
    if (days === 0) return { label: "Due today!", cls: "soon" };
    if (days <= 3)  return { label: "Due in " + days + "d", cls: "soon" };
    return { label: "Due " + new Date(dueDate).toLocaleDateString("en-US",{month:"short",day:"numeric"}), cls: "ok" };
  };

  const TEMPLATES = [
    { icon:"🌐", name:"Web App",       tech:"React · Node.js · MongoDB",     tags:["frontend","backend","fullstack"], desc:"Full-stack web application" },
    { icon:"📱", name:"Mobile App",    tech:"React Native · Firebase",        tags:["mobile","ios","android"],        desc:"Cross-platform mobile app" },
    { icon:"🤖", name:"ML Project",    tech:"Python · TensorFlow · Pandas",   tags:["ml","ai","python"],              desc:"Machine learning project" },
    { icon:"🔌", name:"REST API",      tech:"Node.js · Express · PostgreSQL", tags:["api","backend","rest"],          desc:"RESTful API backend" },
    { icon:"🎨", name:"UI Design",     tech:"Figma · Tailwind CSS",           tags:["design","ui","css"],             desc:"Frontend UI design project" },
    { icon:"📊", name:"Data Analysis", tech:"Python · Pandas · Matplotlib",   tags:["data","analysis","python"],      desc:"Data analysis & visualization" },
  ];

  const applyTemplate = (tpl) => {
    setFTitle(tpl.name + " Project");
    setFDesc(tpl.desc);
    setFTech(tpl.tech);
    setFTags(tpl.tags.join(", "));
    setShowTemplates(false);
    toast.success("Template applied! ✅");
  };

  /* ── BATCH 4: ACTIVITY LOG ── */
  const fetchActivity = async (projectId) => {
    setLoadingActivity(true);
    try {
      const res = await API.get("/projects/" + projectId + "/activity");
      setActivityLog(res.data.activity || []);
    } catch { setActivityLog([]); }
    finally { setLoadingActivity(false); }
  };

  const ACTIVITY_ICONS = {
    created: "🚀", updated: "✏️", commented: "💬",
    file_uploaded: "📎", restored: "♻️", shared: "🔗", pinned: "📌",
  };

  /* ── BATCH 4: @MENTIONS ── */
  const handleCommentChange = async (val) => {
    setNewComment(val);
    const match = val.match(/@(\w*)$/);
    if (match && match[1].length >= 1) {
      setMentionQuery(match[1]);
      setShowMentions(true);
      setMentionIdx(0);
      try {
        const q = encodeURIComponent(match[1]);
        const res = await API.get("/users/search?q=" + q);
        setMentionResults(res.data.users || []);
      } catch { setMentionResults([]); }
    } else {
      setShowMentions(false);
      setMentionResults([]);
    }
  };

  const insertMention = (user) => {
    const replaced = newComment.replace(/@\w*$/, "@" + user.name + " ");
    setNewComment(replaced);
    setShowMentions(false);
    setMentionResults([]);
    commentInputRef.current?.focus();
  };

  const handleCommentKey = (e) => {
    if (showMentions) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx(i => Math.min(i+1, mentionResults.length-1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setMentionIdx(i => Math.max(i-1, 0)); return; }
      if (e.key === "Enter" && mentionResults[mentionIdx]) { e.preventDefault(); insertMention(mentionResults[mentionIdx]); return; }
      if (e.key === "Escape") { setShowMentions(false); return; }
    }
    if (e.key === "Enter" && !e.shiftKey && !showMentions) addComment();
  };

  const renderCommentText = (text) => {
    const parts = text.split(/(@\w+)/g);
    return parts.map((p, i) =>
      p.startsWith("@")
        ? <span key={i} className="vs-mention">{p}</span>
        : p
    );
  };

  /* ── BATCH 4: TEAMS ── */
  const fetchTeams = async () => {
    try {
      const res = await API.get("/teams");
      setTeams(res.data.teams || []);
    } catch { setTeams([]); }
  };

  useEffect(() => { if (activeNav === "teams") fetchTeams(); }, [activeNav]);
  useEffect(() => { if (activeNav === "certificates") fetchCertificates(); }, [activeNav]);

  const fetchCertificates = async () => {
    try {
      const res = await API.get("/projects?tag=certificate&limit=50");
      setCertificates(res.data.projects || []);
    } catch { setCertificates([]); }
  };

  const createCertificate = async () => {
    if (!certName.trim()) { toast.error("Certificate name is required"); return; }
    setLoading(true);
    try {
      const tagsArr = ["certificate", ...certTags.split(",").map(t=>t.trim()).filter(Boolean)];
      const fd = new FormData();
      fd.append("title", certName);
      fd.append("description", certIssuer ? "Issued by: " + certIssuer : "");
      fd.append("tags", JSON.stringify(tagsArr));
      fd.append("status", "completed");
      if (certDate) fd.append("dueDate", certDate);
      certFiles.forEach(f => fd.append("files", f));
      await API.post("/projects", fd);
      toast.success("Certificate added! 🏆");
      setShowCertModal(false);
      setCertName(""); setCertIssuer(""); setCertDate(""); setCertTags(""); setCertFiles([]);
      fetchCertificates();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { setLoading(false); }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) { toast.error("Team name required"); return; }
    try {
      await API.post("/teams", { name: newTeamName });
      toast.success("Team created! 🎉");
      setNewTeamName(""); setShowCreateTeam(false);
      fetchTeams();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
  };

  const inviteToTeam = async (teamId) => {
    if (!teamInviteEmail.trim()) { toast.error("Enter an email address"); return; }
    setInvitingToTeam(true);
    try {
      await API.post("/teams/" + teamId + "/invite", { email: teamInviteEmail });
      toast.success("Member added! ✅");
      setTeamInviteEmail("");
      // refresh team data
      const res = await API.get("/teams");
      setTeams(res.data.teams || []);
      // update selected team
      setSelectedTeam(prev => {
        const updated = res.data.teams.find(t => t._id === prev?._id);
        return updated || prev;
      });
    } catch (e) { toast.error(e.response?.data?.message || "Failed to add member"); }
    finally { setInvitingToTeam(false); }
  };

  const removeFromTeam = async (teamId, memberId, memberName) => {
    if (!window.confirm("Remove " + memberName + " from this team?")) return;
    try {
      await API.delete("/teams/" + teamId + "/members/" + memberId);
      toast.success(memberName + " removed");
      const res = await API.get("/teams");
      setTeams(res.data.teams || []);
      setSelectedTeam(prev => {
        const updated = res.data.teams.find(t => t._id === prev?._id);
        return updated || prev;
      });
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
  };

  const deleteTeam = async (teamId, teamName) => {
    if (!window.confirm("Delete team \"" + teamName + "\"? This cannot be undone.")) return;
    try {
      await API.delete("/teams/" + teamId);
      toast.success("Team deleted");
      setSelectedTeam(null);
      fetchTeams();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
  };

  const ONBOARD_STEPS = [
    { icon:"🏛️", title:"Welcome to VaultSphere!", desc:"Your personal project vault. Store, organise and share your work — all in one place." },
    { icon:"📁", title:"Create your first project", desc:"Hit the '+ New Project' button or press N anywhere. Add a title, description, tech stack, files and tags." },
    { icon:"🤝", title:"Collaborate with teammates", desc:"Go to Collaborate tab to search users and send invites. Accept or decline invites you receive." },
    { icon:"🔍", title:"Find anything instantly", desc:"Press / to open global search. Search across all your projects by title, tech stack or tags." },
  ];

  const finishOnboarding = () => {
    localStorage.setItem("vs_onboarded", "1");
    setShowOnboarding(false);
  };

  /* ══════════════════════════════
     AI FILE SUMMARISER
  ══════════════════════════════ */
  const summariseFile = async (file, projectTitle) => {
    setAiModal({ file, projectTitle });
    setAiResult(null);
    setAiLoading(true);

    try {
      const ft = getFileType(file.originalName || "");
      const ext = (file.originalName || "").split(".").pop().toLowerCase();

      let body = {};

      if (ft === "img") {
        // Send URL — backend fetches and converts to base64
        body = {
          fileName: file.originalName,
          projectTitle,
          isImage: true,
          fileUrl: file.url,
          mimeType: "image/" + (ext === "jpg" ? "jpeg" : ext || "png"),
        };
      } else if (ext === "pdf") {
        // PDFs: send URL and let backend handle it as text description
        body = {
          fileName: file.originalName,
          projectTitle,
          isImage: false,
          fileContent: "[PDF file — analyse based on the file name: " + file.originalName + " — describe what this PDF likely contains and how it would be used in a project called: " + projectTitle + "]",
        };
      } else {
        // Text / code files — fetch content client-side and truncate
        try {
          const response = await fetch(file.url);
          const text = await response.text();
          body = {
            fileName: file.originalName,
            projectTitle,
            isImage: false,
            fileContent: text.slice(0, 8000),
          };
        } catch {
          body = {
            fileName: file.originalName,
            projectTitle,
            isImage: false,
            fileContent: "[Could not read file content — analyse based on file name: " + file.originalName + "]",
          };
        }
      }

      const res = await API.post("/ai/summarise", body);
      setAiResult(res.data.result);

    } catch (e) {
      toast.error("AI analysis failed — try again");
      setAiModal(null);
    } finally {
      setAiLoading(false);
    }
  };

  /* ══════════════════════════════
     ANALYTICS
  ══════════════════════════════ */
  const fetchAnalytics = async () => {
    try {
      // Build analytics from existing data
      const [projRes, statsRes] = await Promise.all([
        API.get("/projects?limit=100"),
        API.get("/users/stats"),
      ]);
      const allProjects = projRes.data.projects || [];
      const stats       = statsRes.data;

      // Files uploaded per month (last 6 months)
      const now = new Date();
      const months = Array.from({length: 6}, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        return { label: d.toLocaleString("en-US", {month:"short"}), year: d.getFullYear(), month: d.getMonth(), count: 0 };
      });

      allProjects.forEach(p => {
        (p.files || []).forEach(f => {
          const d = new Date(f.uploadedAt || p.createdAt);
          months.forEach(m => {
            if (d.getFullYear() === m.year && d.getMonth() === m.month) m.count++;
          });
        });
      });

      // Folders created per month
      const folderMonths = Array.from({length: 6}, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        return { label: d.toLocaleString("en-US", {month:"short"}), year: d.getFullYear(), month: d.getMonth(), count: 0 };
      });
      allProjects.forEach(p => {
        const d = new Date(p.createdAt);
        folderMonths.forEach(m => {
          if (d.getFullYear() === m.year && d.getMonth() === m.month) m.count++;
        });
      });

      // Status breakdown
      const statusCount = {};
      allProjects.forEach(p => { statusCount[p.status||"active"] = (statusCount[p.status||"active"]||0) + 1; });

      // Top folders by file count
      const topFolders = [...allProjects]
        .sort((a,b) => (b.files||[]).length - (a.files||[]).length)
        .slice(0, 5);

      // Activity contribution grid (last 52 weeks)
      const grid = Array(364).fill(0);
      allProjects.forEach(p => {
        const created = new Date(p.createdAt);
        const daysAgo = Math.floor((now - created) / 86400000);
        if (daysAgo < 364) grid[363 - daysAgo]++;
        (p.files || []).forEach(f => {
          const uploaded = new Date(f.uploadedAt || p.createdAt);
          const da = Math.floor((now - uploaded) / 86400000);
          if (da < 364) grid[363 - da]++;
        });
      });

      setAnalyticsData({
        fileMonths: months,
        folderMonths,
        statusCount,
        topFolders,
        grid,
        totalFiles: allProjects.reduce((a, p) => a + (p.files||[]).length, 0),
        totalFolders: allProjects.length,
        daysActive: stats.daysOnPlatform || 0,
        badges: stats.badges || [],
        sharedCount: sharedProjects.length,
      });
    } catch { toast.error("Failed to load analytics"); }
  };

  useEffect(() => { if (activeNav === "analytics") fetchAnalytics(); }, [activeNav]);

  /* ═══════════════════
     RENDER
  ═══════════════════ */
  return (
    <div className="vs-shell">

      {/* SIDEBAR */}
      <aside className="vs-sidebar">
        <div className="vs-logo">VaultSphere</div>
        <nav className="vs-nav">
          {NAV.map(n=>(
            <button key={n.key} className={"vs-nav-btn" + (activeNav===n.key?" active":"")}
              onClick={()=>n.key==="profile"?navigate("/profile"):setActiveNav(n.key)}>
              <span className="vs-nav-icon">{n.icon}</span>
              {t(n.key) || n.label}
              {n.key==="collab"&&pendingCount>0&&<span className="vs-nav-badge">{pendingCount}</span>}
              {n.key==="bin"&&binCount>0&&<span className="vs-nav-badge">{binCount}</span>}
            </button>
          ))}
        </nav>
        {/* Bell — visible on both desktop and mobile top bar */}
        <div className="vs-bell-wrap" style={{padding:"0 12px 4px"}}>
          <button className="vs-bell-btn vs-notif-bell" onClick={()=>setShowNotifPanel(p=>!p)} title="Notifications">
            🔔
            {unreadCount>0&&<span className="vs-bell-dot"/>}
          </button>
        </div>
        <div className="vs-sidebar-footer" onClick={logout}>
          <div className="vs-sidebar-avatar">{initials(user?.name)}</div>
          <span className="vs-sidebar-username">{user?.name||"User"}</span>
          <span style={{color:"var(--text-3)",fontSize:13}}>↩</span>
        </div>
      </aside>

      {/* MAIN */}
      <main className="vs-main" key={activeNav}>

        {/* ══ PROJECTS ══ */}
        {activeNav==="projects"&&(
          <>
            <div className="vs-topbar">
              <div>
                <h1 className="vs-page-title">{t("dashboard")}</h1>
                <p className="vs-page-subtitle">
                  {dashTab==="mine"
                    ? projects.length + " folder" + (projects.length!==1?"s":"") + " · Page " + currentPage + " of " + totalPages
                    : sharedProjects.length + " folder" + (sharedProjects.length!==1?"s":"") + " shared with you"
                  }
                </p>
              </div>
              <div className="vs-topbar-actions">
                <button className="vs-btn-ghost" style={{gap:6,display:"flex",alignItems:"center"}} onClick={()=>setShowSearch(true)} title="Search (press /)">
                  🔍 <span style={{fontSize:12}}>Search</span>
                  <span style={{padding:"2px 6px",background:"var(--surface-2)",borderRadius:6,fontSize:10,fontWeight:700,color:"var(--text-3)"}}>/</span>
                </button>
                <button className="vs-btn-ghost" style={{padding:"9px 12px"}} onClick={()=>setShowShortcuts(p=>!p)} title="Keyboard shortcuts (press ?)">⌨️</button>
                {dashTab==="mine"&&<button className="vs-btn-primary" onClick={openCreate}>+ New Folder</button>}
              </div>
            </div>

            {/* STATS BAR */}
            {dashStats && (
              <div className="vs-stats-bar">
                {[
                  {icon:"📁", num: dashStats.totalProjects,     label:"My Folders"},
                  {icon:"🤝", num: sharedProjects.length,       label:"Shared with Me"},
                  {icon:"🗓️", num: dashStats.daysOnPlatform,    label:"Days Active"},
                  {icon:"🏆", num: (dashStats.badges||[]).length, label:"Badges Earned"},
                ].map((s,i)=>(
                  <div key={i} className="vs-stat-widget" style={{animationDelay:(i*.06) + "s"}}>
                    <div className="vs-stat-widget-icon">{s.icon}</div>
                    <div>
                      <div className="vs-stat-widget-num">{s.num}</div>
                      <div className="vs-stat-widget-label">{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* DASH TABS */}
            <div className="vs-dash-tabs">
              <button className={"vs-dash-tab" + (dashTab==="mine"?" active":"")} onClick={()=>setDashTab("mine")}>
                📁 My Folders
                <span className="vs-dash-tab-count">{projects.length}</span>
              </button>
              <button className={"vs-dash-tab" + (dashTab==="shared"?" active":"")} onClick={()=>setDashTab("shared")}>
                🤝 Shared with Me
                {sharedProjects.length>0&&<span className="vs-dash-tab-count">{sharedProjects.length}</span>}
              </button>
            </div>

            {/* ── MY FOLDERS TAB ── */}
            {dashTab==="mine"&&(
              <>
                {/* SURVEY REMINDER BANNER */}
                {showSurveyBanner&&(
                  <div className="vs-survey-banner">
                    <span className="vs-survey-banner-icon">📝</span>
                    <div className="vs-survey-banner-text">
                      <div className="vs-survey-banner-title">Help us improve VaultSphere!</div>
                      <div className="vs-survey-banner-sub">Takes only 2 minutes · Your feedback trains our AI model 🤖</div>
                    </div>
                    <button className="vs-survey-banner-btn" onClick={()=>navigate("/survey")}>Take Survey →</button>
                    <button className="vs-survey-banner-close" onClick={dismissSurveyBanner} title="Dismiss">✕</button>
                  </div>
                )}
                <div className="vs-filter-row">
                  <input className="vs-input" placeholder="Search folders…" value={search} onChange={e=>setSearch(e.target.value)}/>
                </div>

            {/* PINNED BAR */}
            {pinnedProjects.length>0&&(
              <div className="vs-pinned-bar">
                <div className="vs-pinned-label">📌 Pinned Folders</div>
                <div className="vs-pinned-row">
                  {pinnedProjects.map(p=>(
                    <div key={p._id} className="vs-pinned-card" onClick={()=>openDrawer(p)}>
                      <div style={{fontSize:22,marginBottom:4}}>📁</div>
                      <div className="vs-pinned-card-title">{p.title}</div>
                      <div className="vs-pinned-card-tech">📎 {(p.files||[]).length} file{(p.files||[]).length!==1?"s":""}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loadingProjects ? (
              <div className="vs-folder-grid">
                {[1,2,3,4,5,6].map(i=>(
                  <div key={i} className="vs-skeleton-card" style={{height:160,opacity:1-i*.1,borderRadius:18}}>
                    <div className="vs-skeleton" style={{width:40,height:40,borderRadius:10,marginBottom:12}}/>
                    <div className="vs-skeleton vs-skeleton-line long" style={{marginBottom:8}}/>
                    <div className="vs-skeleton vs-skeleton-line med"/>
                  </div>
                ))}
              </div>
            ) : projects.length===0 ? (
              <div className="vs-empty-state">
                <svg className="vs-empty-state-svg" width="160" height="140" viewBox="0 0 160 140" fill="none">
                  <rect x="10" y="45" width="140" height="85" rx="14" strokeWidth="1.5" style={{fill:"var(--surface-2)",stroke:"var(--border-strong)"}}/>
                  <path d="M10 60 Q10 45 25 45 L60 45 L70 32 L135 32 Q150 32 150 47 L150 60Z" style={{fill:"var(--border-strong)"}}/>
                  <rect x="30" y="68" width="40" height="8" rx="4" opacity=".5" style={{fill:"var(--accent)"}}/>
                  <rect x="30" y="82" width="100" height="6" rx="3" style={{fill:"var(--border-strong)"}}/>
                  <rect x="30" y="94" width="75" height="6" rx="3" style={{fill:"var(--border-strong)"}}/>
                  <circle cx="130" cy="45" r="18" style={{fill:"var(--accent)"}}/>
                  <text x="130" y="51" textAnchor="middle" fontSize="18" style={{fill:"var(--on-accent)"}}>+</text>
                </svg>
                <div className="vs-empty-state-title">No folders yet</div>
                <div className="vs-empty-state-sub">Create your first folder to organise your files and projects.</div>
                <button className="vs-btn-primary" onClick={openCreate}>+ Create First Folder</button>
              </div>
            ) : (
              <div className="vs-folder-grid">
                {projects.map((p,i)=>{
                  const due = getDueLabel(p.dueDate);
                  const fileCount = (p.files||[]).length;
                  const folderIcon = p.status==="completed" ? "✅" : p.status==="on_hold" ? "⏸️" : p.status==="archived" ? "📦" : "📁";
                  return (
                    <div key={p._id} className="vs-folder-card" style={{animationDelay:(i*.04)+"s"}} onClick={()=>openDrawer(p)}>
                      <button className={"vs-folder-pin" + (p.pinned?" pinned":"")} onClick={e=>togglePin(e,p._id,p.pinned)} title={p.pinned?"Unpin":"Pin"}>📌</button>
                      <div className="vs-folder-icon">{folderIcon}</div>
                      <div className="vs-folder-name">{p.title}</div>
                      {p.description&&<div className="vs-folder-desc">{p.description}</div>}
                      <div className="vs-folder-meta">
                        <span className="vs-folder-file-count">📎 {fileCount} file{fileCount!==1?"s":""}</span>
                        {p.status&&p.status!=="active"&&<span className={"vs-status " + p.status} style={{fontSize:10}}>{STATUS_ICONS[p.status]} {STATUS_LABELS[p.status]}</span>}
                        {due&&<span className={"vs-due " + due.cls} style={{fontSize:10}}>⏰ {due.label}</span>}
                      </div>
                      {(p.tags||[]).length>0&&(
                        <div className="vs-tags" style={{marginTop:8,gap:4}}>
                          {(p.tags||[]).slice(0,3).map(tag=><span key={tag} className="vs-tag" style={{fontSize:10,padding:"2px 8px"}}>#{tag}</span>)}
                        </div>
                      )}
                      <div className="vs-folder-actions" onClick={e=>e.stopPropagation()}>
                        <button className="vs-btn-sm vs-btn-edit" onClick={e=>{e.stopPropagation();openEdit(p);}}>Edit</button>
                        <button className="vs-btn-sm vs-btn-del"  onClick={e=>{e.stopPropagation();softDelete(p._id);}}>Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {totalPages>1&&(
              <div className="vs-pagination">
                <button className="vs-page-btn" onClick={()=>fetchProjects(currentPage-1)} disabled={currentPage===1}>← Prev</button>
                <span className="vs-page-info">Page {currentPage} of {totalPages}</span>
                <button className="vs-page-btn" onClick={()=>fetchProjects(currentPage+1)} disabled={currentPage===totalPages}>Next →</button>
              </div>
            )}
              </>
            )}

            {/* ── SHARED WITH ME TAB ── */}
            {dashTab==="shared"&&(
              <>
                {sharedProjects.length===0 ? (
                  <div className="vs-empty-state">
                    <svg className="vs-empty-state-svg" width="160" height="140" viewBox="0 0 160 140" fill="none">
                      <rect x="15" y="45" width="130" height="80" rx="14" strokeWidth="1.5" style={{fill:"var(--surface-2)",stroke:"var(--border-strong)"}}/>
                      <path d="M15 60 Q15 45 28 45 L55 45 L65 33 L132 33 Q145 33 145 46 L145 60Z" style={{fill:"var(--border-strong)"}}/>
                      <circle cx="42" cy="88" r="14" style={{fill:"var(--border-strong)"}}/>
                      <circle cx="80" cy="88" r="14" style={{fill:"var(--border-strong)"}}/>
                      <line x1="56" y1="88" x2="66" y2="88" strokeWidth="2" style={{stroke:"var(--surface)"}}/>
                    </svg>
                    <div className="vs-empty-state-title">No shared folders yet</div>
                    <div className="vs-empty-state-sub">When someone invites you to collaborate on a folder and you accept, it will appear here.</div>
                  </div>
                ) : (
                  <div className="vs-folder-grid">
                    {sharedProjects.map((p,i)=>{
                      const fileCount = (p.files||[]).length;
                      const due = getDueLabel(p.dueDate);
                      return (
                        <div key={p._id} className="vs-folder-card shared" style={{animationDelay:(i*.04)+"s"}} onClick={()=>openDrawer(p)}>
                          <div className="vs-folder-icon">📂</div>
                          <div className="vs-folder-name">{p.title}</div>
                          {p.description&&<div className="vs-folder-desc">{p.description}</div>}
                          <div className="vs-folder-meta">
                            <span className="vs-folder-file-count">📎 {fileCount} file{fileCount!==1?"s":""}</span>
                            <span className={"vs-role-chip " + (p._role||"viewer")}>{p._role||"viewer"}</span>
                            {p.status&&p.status!=="active"&&<span className={"vs-status " + p.status} style={{fontSize:10}}>{STATUS_ICONS[p.status]} {STATUS_LABELS[p.status]}</span>}
                            {due&&<span className={"vs-due " + due.cls} style={{fontSize:10}}>⏰ {due.label}</span>}
                          </div>
                          <div className="vs-shared-by">
                            🤝 Shared by {p._sharedBy?.name||"Unknown"}
                          </div>
                          {(p.tags||[]).length>0&&(
                            <div className="vs-tags" style={{marginTop:8,gap:4}}>
                              {(p.tags||[]).slice(0,3).map(tag=><span key={tag} className="vs-tag" style={{fontSize:10,padding:"2px 8px"}}>#{tag}</span>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ══ TEAMS ══ */}
        {activeNav==="teams"&&(
          <>
            <div className="vs-topbar">
              <div>
                <h1 className="vs-page-title">Teams</h1>
                <p className="vs-page-subtitle">Create teams and invite members to collaborate</p>
              </div>
              <div className="vs-topbar-actions">
                <button className="vs-btn-primary" onClick={()=>setShowCreateTeam(p=>!p)}>+ New Team</button>
              </div>
            </div>

            {showCreateTeam&&(
              <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:16,padding:20,marginBottom:18}}>
                <div style={{fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:14}}>Create a New Team</div>
                <input className="vs-input" placeholder="Team name e.g. Design Squad, Backend Team…" value={newTeamName} onChange={e=>setNewTeamName(e.target.value)} onKeyPress={e=>e.key==="Enter"&&createTeam()} style={{marginBottom:12}}/>
                <div style={{display:"flex",gap:10}}>
                  <button className="vs-btn-primary" onClick={createTeam}>Create Team</button>
                  <button className="vs-btn-ghost" onClick={()=>{setShowCreateTeam(false);setNewTeamName("");}}>Cancel</button>
                </div>
              </div>
            )}

            {teams.length===0&&!showCreateTeam?(
              <div className="vs-empty-state">
                <svg className="vs-empty-state-svg" width="150" height="130" viewBox="0 0 150 130" fill="none">
                  <circle cx="55" cy="50" r="22" strokeWidth="1.5" style={{fill:"var(--surface-2)",stroke:"var(--border-strong)"}}/>
                  <circle cx="95" cy="50" r="22" strokeWidth="1.5" style={{fill:"var(--surface-2)",stroke:"var(--border-strong)"}}/>
                  <circle cx="75" cy="80" r="22" strokeWidth="1.5" style={{fill:"var(--surface-3)",stroke:"var(--border-strong)"}}/>
                  <text x="55" y="56" textAnchor="middle" fontSize="18" style={{fill:"var(--accent)"}}>A</text>
                  <text x="95" y="56" textAnchor="middle" fontSize="18" style={{fill:"var(--accent)"}}>B</text>
                  <text x="75" y="86" textAnchor="middle" fontSize="18" style={{fill:"var(--accent)"}}>C</text>
                </svg>
                <div className="vs-empty-state-title">No teams yet</div>
                <div className="vs-empty-state-sub">Create a team to collaborate with your group on shared projects.</div>
                <button className="vs-btn-primary" onClick={()=>setShowCreateTeam(true)}>+ Create First Team</button>
              </div>
            ):(teams.length>0&&(
              <div className="vs-team-layout">
                <div className="vs-team-list-panel">
                  {teams.map((team,i)=>(
                    <div key={team._id||i} className={"vs-team-card" + (selectedTeam?._id===team._id?" active":"")} onClick={()=>{setSelectedTeam(team);setTeamInviteEmail("");}}>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <div className="vs-team-avatar">👥</div>
                        <div>
                          <div className="vs-team-name">{team.name}</div>
                          <div className="vs-team-members">{(team.members||[]).length} member{(team.members||[]).length!==1?"s":""}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  {!selectedTeam?(
                    <div className="vs-team-manage-panel">
                      <div style={{textAlign:"center",padding:"40px 0",color:"var(--text-3)",fontSize:14}}>
                        <div style={{fontSize:36,marginBottom:10,opacity:.4}}>👈</div>
                        Select a team to manage it
                      </div>
                    </div>
                  ):(
                    <div className="vs-team-manage-panel">
                      <div className="vs-manage-title">{selectedTeam.name}</div>
                      <div className="vs-manage-sub">{(selectedTeam.members||[]).length} member{(selectedTeam.members||[]).length!==1?"s":""} · Owner: {selectedTeam.owner?.name||"You"}</div>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>Members</div>
                      {(selectedTeam.members||[]).map((m,i)=>{
                        const isOwner = selectedTeam.owner?._id===m._id||selectedTeam.owner===m._id;
                        const isMe = user?._id===m._id||user?.id===m._id;
                        return (
                          <div key={m._id||i} className="vs-member-row">
                            <div className="vs-member-av">{m.avatar?.url?<img src={m.avatar.url} alt={m.name}/>:(m.name||"?").charAt(0).toUpperCase()}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <div className="vs-member-name">{m.name||"Unknown"} {isMe&&<span style={{fontSize:10,color:"var(--text-3)"}}>(you)</span>}</div>
                              <div className="vs-member-email">{m.email||""}</div>
                            </div>
                            {isOwner?(<span className="vs-owner-badge">👑 Owner</span>):(!isMe&&(<button className="vs-remove-btn" onClick={()=>removeFromTeam(selectedTeam._id,m._id,m.name)}>Remove</button>))}
                          </div>
                        );
                      })}
                      <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--border)"}}>
                        <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".8px",marginBottom:10}}>Invite someone</div>
                        <div className="vs-invite-row">
                          <input className="vs-input" placeholder="Enter email address…" value={teamInviteEmail} onChange={e=>setTeamInviteEmail(e.target.value)} onKeyPress={e=>e.key==="Enter"&&inviteToTeam(selectedTeam._id)} style={{flex:1}}/>
                          <button className="vs-btn-primary" onClick={()=>inviteToTeam(selectedTeam._id)} disabled={invitingToTeam||!teamInviteEmail.trim()}>{invitingToTeam?"Adding…":"+ Add"}</button>
                        </div>
                        <p style={{fontSize:11,color:"var(--text-3)",marginTop:8}}>The person must already have a VaultSphere account</p>
                      </div>
                      {(selectedTeam.owner?._id===user?._id||selectedTeam.owner?._id===user?.id||selectedTeam.owner===user?._id||selectedTeam.owner===user?.id)&&(
                        <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--border)"}}>
                          <button className="vs-btn-ghost" style={{borderColor:"var(--danger-border)",color:"var(--danger)",fontSize:12}} onClick={()=>deleteTeam(selectedTeam._id,selectedTeam.name)}>🗑️ Delete Team</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* ══ RECYCLE BIN ══ */}
        {activeNav==="bin"&&(
          <>
            <div className="vs-topbar">
              <div>
                <h1 className="vs-page-title">Recycle Bin</h1>
                <p className="vs-page-subtitle">Restore or permanently delete projects</p>
              </div>
            </div>
            {binProjects.length===0?(
              <div className="vs-empty-state">
                <svg className="vs-empty-state-svg" width="140" height="120" viewBox="0 0 140 120" fill="none">
                  <rect x="30" y="25" width="80" height="80" rx="12" strokeWidth="1.5" style={{fill:"var(--surface-2)",stroke:"var(--border-strong)"}}/>
                  <rect x="20" y="25" width="100" height="12" rx="6" style={{fill:"var(--border-strong)"}}/>
                  <line x1="55" y1="50" x2="55" y2="90" strokeWidth="2" strokeLinecap="round" style={{stroke:"var(--accent)"}}/>
                  <line x1="70" y1="50" x2="70" y2="90" strokeWidth="2" strokeLinecap="round" style={{stroke:"var(--accent)"}}/>
                  <line x1="85" y1="50" x2="85" y2="90" strokeWidth="2" strokeLinecap="round" style={{stroke:"var(--accent)"}}/>
                </svg>
                <div className="vs-empty-state-title">Recycle bin is empty</div>
                <div className="vs-empty-state-sub">Deleted projects will appear here. You can restore or permanently remove them.</div>
              </div>
            ):binProjects.map((p,i)=>(
              <div key={p._id} className="vs-bin-card" style={{animationDelay:(i*.04) + "s"}}>
                <div style={{flex:1}}>
                  <div className="vs-bin-name">{p.title}</div>
                  <div className="vs-bin-meta">Deleted {new Date(p.deletedAt||p.updatedAt).toLocaleDateString()}</div>
                </div>
                <div className="vs-bin-actions">
                  <button className="vs-btn-sm vs-btn-restore" onClick={()=>restoreProject(p._id)}>↩ Restore</button>
                  <button className="vs-btn-sm vs-btn-permdel" onClick={()=>permanentDelete(p._id)}>✕ Delete Forever</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ══ ANALYTICS ══ */}
        {activeNav==="analytics"&&(
          <>
            <div className="vs-topbar">
              <div>
                <h1 className="vs-page-title">Analytics</h1>
                <p className="vs-page-subtitle">Your personal VaultSphere activity dashboard</p>
              </div>
              <button className="vs-btn-ghost" onClick={fetchAnalytics}>↻ Refresh</button>
            </div>

            {!analyticsData ? (
              <div className="vs-empty-state"><div className="vs-empty-icon">⏳</div>Loading analytics…</div>
            ) : (
              <>
                {/* TOP STAT CARDS */}
                <div className="vs-stats-bar" style={{marginBottom:20}}>
                  {[
                    {icon:"📁", num: analyticsData.totalFolders,  label:"Total Folders"},
                    {icon:"📎", num: analyticsData.totalFiles,    label:"Total Files"},
                    {icon:"🤝", num: analyticsData.sharedCount,   label:"Shared with Me"},
                    {icon:"🗓️", num: analyticsData.daysActive,    label:"Days Active"},
                  ].map((s,i)=>(
                    <div key={i} className="vs-stat-widget" style={{animationDelay:(i*.06)+"s"}}>
                      <div className="vs-stat-widget-icon">{s.icon}</div>
                      <div>
                        <div className="vs-stat-widget-num">{s.num}</div>
                        <div className="vs-stat-widget-label">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CONTRIBUTION GRID — GitHub style */}
                <div className="vs-chart-card" style={{marginBottom:16}}>
                  <div className="vs-chart-title">Activity Heatmap</div>
                  <div className="vs-chart-sub">Folder creations + file uploads over the last year</div>
                  <div className="vs-contribution-grid">
                    {analyticsData.grid.map((val, i) => {
                      const cls = val === 0 ? "" : val === 1 ? "l1" : val === 2 ? "l2" : val === 3 ? "l3" : "l4";
                      return <div key={i} className={"vs-contrib-cell" + (cls?" "+cls:"")} title={val + " activities"}/>;
                    })}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:10,fontSize:11,color:"var(--text-3)"}}>
                    Less
                    {["","l1","l2","l3","l4"].map(c=><div key={c} className={"vs-contrib-cell" + (c?" "+c:"")}/>)}
                    More
                  </div>
                </div>

                <div className="vs-analytics-grid">
                  {/* FILES UPLOADED PER MONTH */}
                  <div className="vs-chart-card">
                    <div className="vs-chart-title">Files Uploaded</div>
                    <div className="vs-chart-sub">Last 6 months</div>
                    <div className="vs-bar-chart">
                      {analyticsData.fileMonths.map((m,i)=>{
                        const max = Math.max(...analyticsData.fileMonths.map(x=>x.count), 1);
                        const h = Math.max((m.count / max) * 80, m.count > 0 ? 4 : 0);
                        return (
                          <div key={i} className="vs-bar-wrap" style={{position:"relative"}}>
                            <div style={{flex:1,display:"flex",alignItems:"flex-end"}}>
                              <div className="vs-bar" style={{height: h + "px", background: i===5?"var(--accent)":"var(--accent)"}}/>
                            </div>
                            <span className="vs-bar-label" style={{position:"static",fontSize:9,color:"var(--text-3)"}}>{m.label}</span>
                            {m.count>0&&<span style={{fontSize:9,color:"var(--accent)",fontWeight:700}}>{m.count}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* FOLDERS CREATED PER MONTH */}
                  <div className="vs-chart-card">
                    <div className="vs-chart-title">Folders Created</div>
                    <div className="vs-chart-sub">Last 6 months</div>
                    <div className="vs-bar-chart">
                      {analyticsData.folderMonths.map((m,i)=>{
                        const max = Math.max(...analyticsData.folderMonths.map(x=>x.count), 1);
                        const h = Math.max((m.count / max) * 80, m.count > 0 ? 4 : 0);
                        return (
                          <div key={i} className="vs-bar-wrap">
                            <div style={{flex:1,display:"flex",alignItems:"flex-end"}}>
                              <div className="vs-bar" style={{height: h + "px", background: i===5?"var(--accent)":"var(--accent-soft)"}}/>
                            </div>
                            <span style={{fontSize:9,color:"var(--text-3)"}}>{m.label}</span>
                            {m.count>0&&<span style={{fontSize:9,color:"var(--text-2)",fontWeight:700}}>{m.count}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* STATUS BREAKDOWN DONUT */}
                  <div className="vs-chart-card">
                    <div className="vs-chart-title">Folder Status</div>
                    <div className="vs-chart-sub">Breakdown by status</div>
                    <div className="vs-donut-wrap">
                      {(()=>{
                        const total = Object.values(analyticsData.statusCount).reduce((a,b)=>a+b,0)||1;
                        const colors = {active:"#2b9244",in_progress:"#f57f17",completed:"#1565c0",on_hold:"#c62828",archived:"#6b4838"};
                        const labels = {active:"Active",in_progress:"In Progress",completed:"Completed",on_hold:"On Hold",archived:"Archived"};
                        const r = 36, cx = 44, cy = 44, circ = 2*Math.PI*r;
                        let offset = 0;
                        const slices = Object.entries(analyticsData.statusCount).map(([k,v])=>{
                          const dash = (v/total)*circ;
                          const slice = {key:k, dash, offset, color:colors[k]||"var(--accent)"};
                          offset += dash;
                          return slice;
                        });
                        return (
                          <>
                            <svg width="88" height="88" style={{flexShrink:0}}>
                              {slices.map((s,i)=>(
                                <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth="14"
                                  strokeDasharray={s.dash + " " + (circ - s.dash)}
                                  strokeDashoffset={-s.offset} transform={"rotate(-90 " + cx + " " + cy + ")"}/>
                              ))}
                              <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" fontSize="13" fontWeight="700" style={{fill:"var(--text)"}}>{total}</text>
                            </svg>
                            <div className="vs-donut-legend">
                              {Object.entries(analyticsData.statusCount).map(([k,v])=>(
                                <div key={k} className="vs-donut-legend-item">
                                  <div className="vs-donut-dot" style={{background:colors[k]||"var(--accent)"}}/>
                                  {labels[k]||k}: <strong>{v}</strong>
                                </div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* TOP FOLDERS */}
                  <div className="vs-chart-card">
                    <div className="vs-chart-title">Most Active Folders</div>
                    <div className="vs-chart-sub">By number of files</div>
                    {analyticsData.topFolders.length===0 ? (
                      <p style={{fontSize:13,color:"var(--text-3)"}}>No folders yet</p>
                    ) : analyticsData.topFolders.map((p,i)=>{
                      const max = (analyticsData.topFolders[0].files||[]).length || 1;
                      const pct = ((p.files||[]).length / max) * 100;
                      return (
                        <div key={p._id} style={{marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                            <span style={{color:"var(--text)",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,marginRight:8}}>{p.title}</span>
                            <span style={{color:"var(--accent)",fontWeight:700,flexShrink:0}}>{(p.files||[]).length} files</span>
                          </div>
                          <div style={{height:6,background:"var(--surface-2)",borderRadius:3,overflow:"hidden"}}>
                            <div style={{height:"100%",width:pct+"%",background:"var(--accent)",borderRadius:3,transition:"width .4s ease"}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* BADGES */}
                {analyticsData.badges.length>0&&(
                  <div className="vs-chart-card">
                    <div className="vs-chart-title">Badges Earned 🏆</div>
                    <div className="vs-chart-sub">{analyticsData.badges.length} of 6 badges unlocked</div>
                    <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:8}}>
                      {analyticsData.badges.map(b=>(
                        <div key={b.id} style={{display:"flex",alignItems:"center",gap:8,background:"var(--surface-2)",borderRadius:12,padding:"10px 14px"}}>
                          <span style={{fontSize:22}}>{b.icon}</span>
                          <div>
                            <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{b.label}</div>
                            <div style={{fontSize:11,color:"var(--text-3)"}}>{b.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ══ COLLABORATION ══ */}
        {activeNav==="collab"&&(
          <>
            <div className="vs-topbar">
              <div>
                <h1 className="vs-page-title">Collaboration</h1>
                <p className="vs-page-subtitle">Invite teammates · manage requests</p>
              </div>
            </div>

            <div className="vs-collab-tabs">
              {[
                {key:"send",     label:"✉️ Send Invite"},
                {key:"received", label:"📬 Received" + (pendingCount>0 ? " (" + pendingCount + ")" : "")},
                {key:"sent",     label:"📤 Sent"},
              ].map(tab=>(
                <button key={tab.key} className={"vs-collab-tab" + (collabTab===tab.key?" active":"")} onClick={()=>setCollabTab(tab.key)}>
                  {tab.label}
                </button>
              ))}
            </div>

            {collabTab==="send"&&(
              <div className="vs-collab-layout">
                <div className="vs-collab-panel">
                  <div className="vs-panel-title">🔍 Find Users</div>
                  <input className="vs-input" style={{marginBottom:12}} placeholder="Type name or email (min 2 chars)…" value={collabQ} onChange={e=>setCollabQ(e.target.value)}/>
                  {searchingUsers&&<p style={{fontSize:13,color:"var(--text-3)",textAlign:"center",padding:"10px 0"}}>Searching…</p>}
                  {!searchingUsers&&collabQ.length>=2&&collabResults.length===0&&<p style={{fontSize:13,color:"var(--text-3)",textAlign:"center",padding:"10px 0"}}>No users found</p>}
                  {collabResults.map(u=>(
                    <div key={u._id} className="vs-user-result">
                      <div className="vs-user-avatar">{initials(u.name)}</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{u.name}</div>
                        <div style={{fontSize:12,color:"var(--text-3)"}}>{u.email}</div>
                      </div>
                      <button className="vs-invite-btn" onClick={()=>{setInvEmail(u.email);toast.info("Email pre-filled ↓");}}>Select →</button>
                    </div>
                  ))}
                  {collabQ.length<2&&<div className="vs-empty" style={{padding:"28px 0"}}><div className="vs-empty-icon">👥</div>Type to search users</div>}
                </div>

                <div className="vs-collab-panel">
                  <div className="vs-panel-title">✉️ Send Invite</div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    <label className="vs-modal-label">Email *</label>
                    <input className="vs-modal-input" placeholder="teammate@email.com" value={invEmail} onChange={e=>setInvEmail(e.target.value)}/>
                    <label className="vs-modal-label">Project (optional)</label>
                    <select className="vs-select" style={{width:"100%",padding:"11px 38px 11px 16px"}} value={invProject} onChange={e=>setInvProject(e.target.value)}>
                      <option value="">— Select a project —</option>
                      {projects.map(p=><option key={p._id} value={p._id}>{p.title}</option>)}
                    </select>
                    <label className="vs-modal-label">Role</label>
                    <select className="vs-select" style={{width:"100%",padding:"11px 38px 11px 16px"}} value={invRole} onChange={e=>setInvRole(e.target.value)}>
                      <option value="viewer">👁 Viewer — view only</option>
                      <option value="editor">✏️ Editor — can edit</option>
                      <option value="admin">⚡ Admin — full access</option>
                    </select>
                    <button className="vs-btn-primary" style={{marginTop:16,alignSelf:"flex-start"}} onClick={sendInvite} disabled={sendingInvite||!invEmail}>
                      {sendingInvite?"Sending…":"Send Invite →"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {collabTab==="received"&&(
              <div style={{maxWidth:680}}>
                {recvInvites.length===0?(
                  <div className="vs-empty"><div className="vs-empty-icon">📭</div>No invitations received</div>
                ):<>
                  <div className="vs-section-label">{pendingCount} pending · {recvInvites.length} total</div>
                  {recvInvites.map((inv,i)=>(
                    <div key={inv._id} className="vs-invite-card" style={{animationDelay:(i*.04) + "s"}}>
                      <div className="vs-user-avatar" style={{background:inv.status==="accepted"?"var(--success)":inv.status==="declined"?"var(--danger)":"var(--accent)"}}>{initials(inv.invitedBy?.name||"?")}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{inv.invitedBy?.name||"Unknown"}</div>
                        <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{inv.project?"Project: " + (inv.project.title):"General collaboration"} · Role: {inv.role}</div>
                        <div style={{fontSize:11,color:"var(--accent)",marginTop:2}}>{new Date(inv.createdAt).toLocaleDateString()}</div>
                      </div>
                      {inv.status==="pending"?(
                        <div className="vs-invite-actions">
                          <button className="vs-btn-accept" onClick={()=>respondInvite(inv._id,"accept")}>Accept</button>
                          <button className="vs-btn-decline" onClick={()=>respondInvite(inv._id,"decline")}>Decline</button>
                        </div>
                      ):<span className={"vs-invite-status " + (inv.status)}>{inv.status}</span>}
                    </div>
                  ))}
                </>}
              </div>
            )}

            {collabTab==="sent"&&(
              <div style={{maxWidth:680}}>
                {sentInvites.length===0?(
                  <div className="vs-empty"><div className="vs-empty-icon">📤</div>No invites sent yet</div>
                ):<>
                  <div className="vs-section-label">{sentInvites.length} invite{sentInvites.length!==1?"s":""} sent</div>
                  {sentInvites.map((inv,i)=>(
                    <div key={inv._id||i} className="vs-invite-card" style={{animationDelay:(i*.04) + "s"}}>
                      <div className="vs-user-avatar" style={{background:inv.status==="accepted"?"var(--success)":"var(--text-3)"}}>{(inv.invitedEmail?.[0]||"?").toUpperCase()}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{inv.invitedEmail}</div>
                        <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{inv.project?"Project: " + (inv.project.title):"General collaboration"} · Role: {inv.role}</div>
                        {inv.createdAt&&<div style={{fontSize:11,color:"var(--accent)",marginTop:2}}>{new Date(inv.createdAt).toLocaleDateString()}</div>}
                      </div>
                      <span className={"vs-invite-status " + (inv.status||"pending")}>{inv.status||"pending"}</span>
                    </div>
                  ))}
                </>}
              </div>
            )}
          </>
        )}

        {/* ══ SETTINGS ══ */}
        {/* ══ CERTIFICATES ══ */}
        {activeNav==="certificates"&&(
          <>
            <div className="vs-topbar">
              <div>
                <h1 className="vs-page-title">🏆 Certificates</h1>
                <p className="vs-page-subtitle">Store and showcase all your certificates in one place</p>
              </div>
              <div className="vs-topbar-actions">
                <button className="vs-btn-primary" onClick={()=>setShowCertModal(true)}>+ Add Certificate</button>
              </div>
            </div>

            {/* ADD CERT MODAL */}
            <Presence show={showCertModal} exitVar="--modal-close-dur">{showCertModal&&(
              <div className="vs-modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowCertModal(false)}>
                <div className="vs-modal">
                  <div className="vs-modal-header">
                    <div className="vs-modal-title">🏆 Add Certificate</div>
                    <button className="vs-modal-close" onClick={()=>setShowCertModal(false)}>✕</button>
                  </div>
                  <label className="vs-modal-label">Certificate Name *</label>
                  <input className="vs-modal-input" placeholder="e.g. AWS Cloud Practitioner, Python Bootcamp…" value={certName} onChange={e=>setCertName(e.target.value)}/>
                  <label className="vs-modal-label">Issued By</label>
                  <input className="vs-modal-input" placeholder="e.g. Coursera, NPTEL, Google, Udemy…" value={certIssuer} onChange={e=>setCertIssuer(e.target.value)}/>
                  <label className="vs-modal-label">Issue Date</label>
                  <input className="vs-modal-input" type="date" value={certDate} onChange={e=>setCertDate(e.target.value)}/>
                  <label className="vs-modal-label">Tags (comma separated)</label>
                  <input className="vs-modal-input" placeholder="e.g. cloud, python, machine-learning" value={certTags} onChange={e=>setCertTags(e.target.value)}/>
                  <label className="vs-modal-label">Upload Certificate File (PDF/Image)</label>
                  <div className="vs-upload-area" style={{position:"relative"}}>
                    <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{position:"absolute",inset:0,opacity:0,cursor:"pointer"}} onChange={e=>setCertFiles(Array.from(e.target.files))}/>
                    <div style={{fontSize:26,marginBottom:4}}>📎</div>
                    <div style={{fontSize:13,color:"var(--text-3)"}}>Upload PDF or image of certificate</div>
                    {certFiles.length>0&&<div className="vs-file-selected">✓ {certFiles.map(f=>f.name).join(", ")}</div>}
                  </div>
                  <div className="vs-modal-btn-row">
                    <button className="vs-btn-primary" onClick={createCertificate} disabled={loading}>{loading?"Adding…":"🏆 Add Certificate"}</button>
                    <button className="vs-btn-ghost" onClick={()=>setShowCertModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}</Presence>

            {certificates.length===0?(
              <div className="vs-empty-state">
                <svg className="vs-empty-state-svg" width="160" height="140" viewBox="0 0 160 140" fill="none">
                  <rect x="20" y="30" width="120" height="90" rx="14" strokeWidth="1.5" style={{fill:"var(--surface-2)",stroke:"var(--border-strong)"}}/>
                  <rect x="20" y="30" width="120" height="18" rx="14" style={{fill:"var(--border-strong)"}}/>
                  <rect x="20" y="44" width="120" height="4" style={{fill:"var(--border-strong)"}}/>
                  <circle cx="80" cy="90" r="22" opacity=".3" style={{fill:"var(--accent)"}}/>
                  <text x="80" y="97" textAnchor="middle" fontSize="24" style={{fill:"var(--accent)"}}>🏆</text>
                  <polygon points="80,20 84,32 97,32 87,40 91,52 80,44 69,52 73,40 63,32 76,32" fill="#f4c542" opacity=".8"/>
                </svg>
                <div className="vs-empty-state-title">No certificates yet</div>
                <div className="vs-empty-state-sub">Add your certificates from Coursera, NPTEL, Google, Udemy and more. Keep them all in one place!</div>
                <button className="vs-btn-primary" onClick={()=>setShowCertModal(true)}>+ Add First Certificate</button>
              </div>
            ):(
              <>
                {/* Stats */}
                <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
                  <div className="vs-stat-widget" style={{flex:1,minWidth:120}}>
                    <div className="vs-stat-widget-icon">🏆</div>
                    <div><div className="vs-stat-widget-num">{certificates.length}</div><div className="vs-stat-widget-label">Total Certificates</div></div>
                  </div>
                  <div className="vs-stat-widget" style={{flex:1,minWidth:120}}>
                    <div className="vs-stat-widget-icon">📎</div>
                    <div><div className="vs-stat-widget-num">{certificates.reduce((a,c)=>a+(c.files||[]).length,0)}</div><div className="vs-stat-widget-label">Files Stored</div></div>
                  </div>
                  <div className="vs-stat-widget" style={{flex:1,minWidth:120}}>
                    <div className="vs-stat-widget-icon">🏷️</div>
                    <div><div className="vs-stat-widget-num">{[...new Set(certificates.flatMap(c=>(c.tags||[]).filter(t=>t!=="certificate")))].length}</div><div className="vs-stat-widget-label">Skills Tagged</div></div>
                  </div>
                </div>

                <button className="vs-add-cert-btn" onClick={()=>setShowCertModal(true)} style={{marginBottom:16}}>
                  <span style={{fontSize:20}}>+</span> Add Another Certificate
                </button>

                <div className="vs-cert-grid">
                  {certificates.map((cert,i)=>{
                    const issuer = cert.description?.replace("Issued by: ","") || "";
                    const otherTags = (cert.tags||[]).filter(t=>t!=="certificate");
                    const emoji = otherTags.some(t=>["aws","cloud","azure","gcp"].includes(t.toLowerCase())) ? "☁️" :
                                  otherTags.some(t=>["python","ml","ai","data"].includes(t.toLowerCase())) ? "🐍" :
                                  otherTags.some(t=>["web","react","html","css","js"].includes(t.toLowerCase())) ? "🌐" :
                                  otherTags.some(t=>["security","cyber"].includes(t.toLowerCase())) ? "🔐" : "🏆";
                    return (
                      <div key={cert._id||i} className="vs-cert-card" onClick={()=>openDrawer(cert)} style={{animationDelay:(i*.05)+"s",animation:"vsSlideUp .3s ease both"}}>
                        <div className="vs-cert-badge">{emoji}</div>
                        <div className="vs-cert-name">{cert.title}</div>
                        {issuer&&<div className="vs-cert-issuer">📋 {issuer}</div>}
                        {cert.dueDate&&<div className="vs-cert-date">📅 {new Date(cert.dueDate).toLocaleDateString("en-US",{month:"long",year:"numeric"})}</div>}
                        {(cert.files||[]).length>0&&<div className="vs-cert-file-count">📎 {(cert.files||[]).length} file{(cert.files||[]).length!==1?"s":""}</div>}
                        {otherTags.length>0&&(
                          <div className="vs-cert-tags">
                            {otherTags.slice(0,3).map(t=><span key={t} className="vs-tag" style={{fontSize:10,padding:"2px 8px"}}>#{t}</span>)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ══ SURVEY ══ */}
        {activeNav==="survey"&&(
          <>
            <div className="vs-topbar">
              <div>
                <h1 className="vs-page-title">📝 {t("survey")}</h1>
                <p className="vs-page-subtitle">{t("shareFeedback")}</p>
              </div>
            </div>
            <div style={{maxWidth:520,margin:"0 auto"}}>
              <div className="vs-settings-card" style={{textAlign:"center",padding:40}}>
                <div style={{fontSize:56,marginBottom:16}}>📝</div>
                <div style={{fontFamily:"var(--font-display)",fontSize:22,color:"var(--text)",marginBottom:10}}>Help us improve VaultSphere</div>
                <p style={{fontSize:14,color:"var(--text-3)",lineHeight:1.7,marginBottom:24}}>
                  Take 2 minutes to share your feedback. Your responses help us build better features for you.
                </p>
                {localStorage.getItem("vs_survey_done") ? (
                  <div style={{padding:"14px 20px",background:"var(--success-tint)",border:"1px solid var(--success-border)",borderRadius:12,color:"var(--success)",fontWeight:600,fontSize:14}}>
                    ✅ You've already completed the survey — thank you!
                  </div>
                ) : (
                  <button className="vs-btn-primary" style={{padding:"14px 32px",fontSize:15}} onClick={()=>navigate("/survey")}>
                    Start Survey →
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* ══ SETTINGS ══ */}
        {activeNav==="settings"&&(
          <>
            <div className="vs-topbar">
              <div><h1 className="vs-page-title">{t("settings")}</h1><p className="vs-page-subtitle">Manage your preferences</p></div>
            </div>

            <div className="vs-settings-card">
              <div className="vs-settings-title">🌐 {t("language")}<div className="vs-settings-line"/></div>
              <p style={{fontSize:13,color:"var(--text-3)",marginBottom:16}}>{t("changeLanguage")} — applies everywhere instantly</p>
              <div className="vs-lang-grid">
                {LANGS.map(l=>(
                  <button key={l.code} className={"vs-lang-option" + (selLang===l.code?" active":"")} onClick={()=>applyLang(l.code)}>
                    <span style={{fontSize:20}}>{l.flag}</span><span>{l.native}</span>
                    {selLang===l.code&&<span className="vs-lang-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="vs-settings-card">
              <div className="vs-settings-title">🔐 Account<div className="vs-settings-line"/></div>
              <p style={{fontSize:13,color:"var(--text-3)",marginBottom:16}}>Update your name or password from your profile page.</p>
              <button className="vs-btn-primary" onClick={()=>navigate("/profile")}>Open Profile →</button>
            </div>

            <div className="vs-settings-card">
              <div className="vs-settings-title">📖 Tutorial<div className="vs-settings-line"/></div>
              <p style={{fontSize:13,color:"var(--text-3)",marginBottom:16}}>Replay the onboarding tutorial to learn VaultSphere features.</p>
              <button className="vs-btn-ghost" onClick={()=>navigate("/tutorial")}>View Tutorial →</button>
            </div>

            <div className="vs-settings-card">
              <div className="vs-settings-title">🚪 {t("signOut")}<div className="vs-settings-line"/></div>
              <p style={{fontSize:13,color:"var(--text-3)",marginBottom:16}}>{t("signOutDesc")}</p>
              <button className="vs-btn-ghost" onClick={logout} style={{borderColor:"var(--danger-border)",color:"var(--danger)"}}>{t("signOutBtn")}</button>
            </div>
          </>
        )}

      </main>

      {/* ══ DRAWER ══ */}
      <Presence show={!!drawer} exitVar="--panel-close-dur">{drawer&&(
        <>
          <div className="vs-drawer-overlay" onClick={()=>setDrawer(null)}/>
          <div className="vs-drawer">
            <div className="vs-drawer-header">
              <div>
                <div className="vs-drawer-title">{drawer.title}</div>
                <div style={{fontSize:12,color:"var(--text-3)",marginTop:4}}>
                  v{drawer.currentVersion||1} · Created {new Date(drawer.createdAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                </div>
              </div>
              <button className="vs-modal-close" onClick={()=>setDrawer(null)}>✕</button>
            </div>

            {/* TABS */}
            <div style={{padding:"12px 26px 0"}}>
              <div className="vs-drawer-tabs">
                <button className={"vs-drawer-tab" + (drawerTab==="details"?" active":"")} onClick={()=>setDrawerTab("details")}>Details</button>
                <button className={"vs-drawer-tab" + (drawerTab==="comments"?" active":"")} onClick={()=>setDrawerTab("comments")}>
                  Comments{drawerComments.length>0?" (" + (drawerComments.length) + ")":""}
                </button>
                <button className={"vs-drawer-tab" + (drawerTab==="activity"?" active":"")} onClick={()=>setDrawerTab("activity")}>Activity</button>
                <button className={"vs-drawer-tab" + (drawerTab==="history"?" active":"")}
                  onClick={()=>{setDrawerTab("history");if(!versions.length)fetchVersions(drawer._id);}}>
                  History
                </button>
              </div>
            </div>

            <div className="vs-drawer-body">

              {/* ── DETAILS TAB ── */}
              {drawerTab==="details"&&(
                <>
                  {drawer.description&&(
                    <div className="vs-drawer-section">
                      <div className="vs-drawer-label">Description</div>
                      <div className="vs-drawer-value">{drawer.description}</div>
                    </div>
                  )}
                  <div className="vs-drawer-section">
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div className="vs-drawer-label" style={{marginBottom:0}}>Files ({(drawer.files||[]).length})</div>
                      <span style={{fontSize:11,color:"var(--text-3)"}}>Click to view · ↓ to download</span>
                    </div>
                    {(drawer.files||[]).length===0 ? (
                      <p style={{fontSize:13,color:"var(--text-3)",marginBottom:8}}>No files yet — upload some below</p>
                    ) : (
                      <div className="vs-file-list">
                        {(drawer.files||[]).map((f,i)=>{
                          const ft = getFileType(f.originalName||"");
                          return (
                            <div key={f._id||i} className="vs-file-row">
                              <div className="vs-file-row-icon">{FICON[ft]}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div className="vs-file-row-name">{f.originalName}</div>
                                <div className="vs-file-row-type">{f.fileType||ft}</div>
                              </div>
                              <div className="vs-file-row-actions">
                                {ft==="img"&&(
                                  <button className="vs-file-action-btn view" onClick={()=>window.open(f.url,"_blank")}>View</button>
                                )}
                                {ft!=="img"&&ft!=="other"&&(
                                  <button className="vs-file-action-btn view" onClick={()=>{
                                    setDrawer(null);
                                    openEditor({...drawer, file:{url:f.url,originalName:f.originalName,_id:f._id}});
                                  }}>Edit</button>
                                )}
                                <button className="vs-file-action-btn ai" onClick={()=>summariseFile(f, drawer.title)}>🤖 AI</button>
                                <button className="vs-file-action-btn dl" onClick={()=>downloadFile(f.url, f.originalName)}>↓</button>
                                <button className="vs-file-action-btn del" onClick={()=>deleteFileFromFolder(drawer._id, f._id, f.originalName)}>✕</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* DROP ZONE — upload more files */}
                    <div className="vs-drop-zone">
                      <input type="file" multiple accept={ALL_EXT.join(",")} onChange={e=>uploadFilesToFolder(drawer._id, e.target.files)}/>
                      <div className="vs-drop-zone-icon">📎</div>
                      <div className="vs-drop-zone-text">Drop files here or click to upload</div>
                      <div className="vs-drop-zone-hint">Any file type · Max 25MB each</div>
                    </div>
                  </div>
                  {(drawer.tags||[]).length>0&&(
                    <div className="vs-drawer-section">
                      <div className="vs-drawer-label">Tags</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:4}}>
                        {(drawer.tags||[]).map(tag=><span key={tag} className="vs-tag">#{tag}</span>)}
                      </div>
                    </div>
                  )}
                  {/* Share Link */}
                  <div className="vs-drawer-section">
                    <div className="vs-drawer-label">Public Share Link</div>
                    <div className="vs-share-toggle" style={{marginTop:6}}>
                      <span className="vs-share-toggle-label">{drawer.shareEnabled ? "Sharing enabled — anyone with the link can view" : "Sharing disabled"}</span>
                      <label className="vs-toggle-switch">
                        <input type="checkbox" checked={!!drawer.shareEnabled} onChange={e=>toggleShare(drawer._id, e.target.checked)}/>
                        <span className="vs-toggle-slider"/>
                      </label>
                    </div>
                    {drawer.shareEnabled && drawer.shareId && (
                      <div className="vs-share-link-box">
                        <span className="vs-share-link-url">{window.location.origin}/share/{drawer.shareId}</span>
                        <button className="vs-share-copy-btn" onClick={()=>copyShareLink(drawer.shareId)}>Copy</button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── COMMENTS TAB ── */}
              {drawerTab==="comments"&&(
                <>
                  {drawerComments.length===0&&<p style={{fontSize:13,color:"var(--text-3)",marginBottom:12}}>No comments yet — type @ to mention someone!</p>}
                  {drawerComments.map((c,i)=>(
                    <div key={c._id||i} className="vs-comment" style={{animationDelay:(i*.03) + "s"}}>
                      <div className="vs-comment-author">{c.author?.name||"Unknown"}</div>
                      <div className="vs-comment-text">{renderCommentText(c.text)}</div>
                      <div className="vs-comment-date">{new Date(c.createdAt).toLocaleString()}</div>
                    </div>
                  ))}
                  <div className="vs-comment-input-row">
                    <div className="vs-comment-input-wrap">
                      <input
                        ref={commentInputRef}
                        className="vs-input"
                        placeholder="Add a comment… type @ to mention"
                        value={newComment}
                        onChange={e=>handleCommentChange(e.target.value)}
                        onKeyDown={handleCommentKey}
                      />
                      <Presence show={showMentions&&mentionResults.length>0} exitVar="--dropdown-close-dur">{showMentions&&mentionResults.length>0&&(
                        <div className="vs-mention-dropdown" style={{bottom:"100%",left:0,right:0,marginBottom:4}}>
                          {mentionResults.map((u,i)=>(
                            <div key={u._id} className={"vs-mention-item" + (i===mentionIdx?" focused":"")} onMouseDown={()=>insertMention(u)}>
                              <div className="vs-mention-av">{u.name.charAt(0).toUpperCase()}</div>
                              <div>
                                <div style={{fontWeight:600}}>{u.name}</div>
                                <div style={{fontSize:11,color:"var(--text-3)"}}>{u.email}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}</Presence>
                    </div>
                    <button className="vs-btn-primary" onClick={addComment} disabled={addingComment||!newComment.trim()}>
                      {addingComment?"…":"Post"}
                    </button>
                  </div>
                </>
              )}

              {/* ── ACTIVITY TAB ── */}
              {drawerTab==="activity"&&(
                <>
                  {loadingActivity&&<div style={{padding:"28px 0",textAlign:"center",color:"var(--text-3)",fontSize:13}}>⏳ Loading…</div>}
                  {!loadingActivity&&activityLog.length===0&&(
                    <div className="vs-empty" style={{padding:"28px 0"}}>
                      <div className="vs-empty-icon">📜</div>
                      No activity yet. Edits, comments and shares appear here.
                    </div>
                  )}
                  {!loadingActivity&&activityLog.length>0&&(
                    <div className="vs-activity-feed">
                      {activityLog.map((a,i)=>(
                        <div key={i} className="vs-activity-entry" style={{animationDelay:(i*.03)+"s",animation:"vsSlideUp .2s ease both"}}>
                          <span className="vs-activity-icon">{ACTIVITY_ICONS[a.type]||"📌"}</span>
                          <div className="vs-activity-avatar">{(a.user?.name||"?").charAt(0).toUpperCase()}</div>
                          <div className="vs-activity-body">
                            <div className="vs-activity-text"><strong>{a.user?.name||"Someone"}</strong> {a.message}</div>
                            <div className="vs-activity-time">{timeAgo(a.createdAt)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── VERSION HISTORY TAB ── */}
              {drawerTab==="history"&&(
                <>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <div style={{fontSize:13,color:"var(--text-3)"}}>
                      {versions.length} saved version{versions.length!==1?"s":""} · Current: v{drawer.currentVersion||1}
                    </div>
                  </div>

                  {loadingVersions&&<div className="vs-empty" style={{padding:"28px 0"}}><div className="vs-empty-icon">⏳</div>Loading history…</div>}

                  {!loadingVersions&&versions.length===0&&(
                    <div className="vs-empty" style={{padding:"28px 0"}}>
                      <div className="vs-empty-icon">🕐</div>
                      No version history yet.<br/>
                      <span style={{fontSize:12,color:"var(--text-3)"}}>History saves automatically when you edit the project.</span>
                    </div>
                  )}

                  {!loadingVersions&&versions.map((v,i)=>(
                    <div key={i} className="vs-version-item" style={{animationDelay:(i*.04) + "s"}}>
                      <div className="vs-version-num">v{v.versionNumber}</div>
                      <div className="vs-version-info">
                        <div className="vs-version-title">{v.title}</div>
                        {v.changeNote&&<div className="vs-version-note">"{v.changeNote}"</div>}
                        <div className="vs-version-meta">
                          {new Date(v.createdAt).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"})}
                          {v.changedBy?.name&&<span> · by {v.changedBy.name}</span>}
                        </div>
                        {v.fileCount>0&&<div style={{fontSize:11,color:"var(--text-3)",marginTop:3}}>📎 {v.fileCount} file{v.fileCount!==1?"s":""} at this point</div>}
                      </div>
                      <button
                        className="vs-version-restore-btn"
                        onClick={()=>restoreVersion(drawer._id, v.versionNumber)}
                        disabled={restoringVersion}
                      >
                        {restoringVersion?"…":"↩ Restore"}
                      </button>
                    </div>
                  ))}
                </>
              )}

            </div>

            <div className="vs-drawer-footer">
              <button className="vs-btn-primary" onClick={()=>{openEdit(drawer);setDrawer(null);}}>✏️ Edit Folder</button>
              <button className="vs-btn-ghost" onClick={()=>softDelete(drawer._id)}>🗑️ Delete</button>
              <button
                className="vs-btn-ghost"
                style={{marginLeft:"auto"}}
                onClick={()=>toggleShare(drawer._id, !drawer.shareEnabled)}
              >
                {drawer.shareEnabled ? "🔗 Sharing On" : "🔗 Share"}
              </button>
            </div>
          </div>
        </>
      )}</Presence>

      {/* ══ INLINE EDITOR ══ */}
      <Presence show={!!editorProj} exitVar="--modal-close-dur">{editorProj&&(
        <div className="vs-modal-overlay" onClick={e=>e.target===e.currentTarget&&!editorLoading&&setEditorProj(null)}>
          <div className="vs-editor-modal">
            <div className="vs-editor-header">
              <div>
                <div className="vs-editor-title">✏️ {editorProj.file?.originalName}</div>
                <div className="vs-editor-meta">
                  {editorProj.title} · {editorProj.file?.originalName?.split(".").pop()?.toUpperCase()} file
                  {!editorLoading&&editorText&&<span style={{marginLeft:8,opacity:.6}}>{editorText.split("\n").length} lines · {editorText.length} chars</span>}
                </div>
              </div>
              {!editorLoading&&<button className="vs-modal-close" onClick={()=>setEditorProj(null)}>✕</button>}
            </div>

            {editorLoading ? (
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:14,background:"var(--bg)",padding:40}}>
                <div style={{width:36,height:36,border:"3px solid var(--border)",borderTopColor:"var(--accent)",borderRadius:"50%",animation:"vsAiSpin .7s linear infinite"}}/>
                <div style={{color:"var(--text-2)",fontSize:13}}>Loading file content…</div>
              </div>
            ) : (
              <div style={{flex:1,display:"flex",overflow:"hidden"}}>
                {/* Line numbers */}
                <div style={{background:"var(--bg)",color:"var(--text-3)",fontSize:13,lineHeight:1.75,padding:"22px 12px 22px 16px",fontFamily:"var(--font-mono)",textAlign:"right",userSelect:"none",minWidth:48,overflowY:"hidden",flexShrink:0}}>
                  {editorText.split("\n").map((_,i)=>(
                    <div key={i}>{i+1}</div>
                  ))}
                </div>
                <textarea
                  className="vs-editor-textarea"
                  style={{flex:1,borderRadius:0,minHeight:0}}
                  value={editorText}
                  onChange={e=>setEditorText(e.target.value)}
                  spellCheck={false}
                  autoFocus
                  onKeyDown={e=>{
                    // Tab key inserts 2 spaces instead of moving focus
                    if (e.key==="Tab") {
                      e.preventDefault();
                      const s=e.target.selectionStart, end=e.target.selectionEnd;
                      const newVal=editorText.substring(0,s)+"  "+editorText.substring(end);
                      setEditorText(newVal);
                      setTimeout(()=>{e.target.selectionStart=e.target.selectionEnd=s+2;},0);
                    }
                  }}
                />
              </div>
            )}

            <div className="vs-editor-footer">
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span className="vs-editor-info">{editorProj.file?.originalName?.split(".").pop()?.toUpperCase()}</span>
                {!editorLoading&&<span className="vs-editor-info" style={{opacity:.5}}>Tab = 2 spaces · Ctrl+S saves</span>}
              </div>
              <div style={{display:"flex",gap:10}}>
                <button className="vs-btn-ghost" onClick={()=>setEditorProj(null)} disabled={editorLoading}>Cancel</button>
                <button className="vs-btn-primary" onClick={saveEditor} disabled={editorSaving||editorLoading}>
                  {editorSaving?"Saving…":"💾 Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}</Presence>

      {/* ══ CREATE / EDIT MODAL ══ */}
      <Presence show={showModal} exitVar="--modal-close-dur">{showModal&&(
        <div className="vs-modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="vs-modal">
            <div className="vs-modal-header">
              <div className="vs-modal-title">{editing ? "✏️ Edit Folder" : "📁 New Folder"}</div>
              <button className="vs-modal-close" onClick={()=>{setShowModal(false);resetForm();}}>✕</button>
            </div>

            {/* TEMPLATE PICKER — only on create */}
            {!editing&&(
              <div style={{marginBottom:14}}>
                <button
                  className="vs-btn-ghost"
                  style={{width:"100%",justifyContent:"center",gap:8,marginBottom:showTemplates?10:0}}
                  onClick={()=>setShowTemplates(p=>!p)}
                >
                  🗂️ {showTemplates?"Hide Templates":"Start from a Template"}
                </button>
                {showTemplates&&(
                  <div className="vs-template-grid">
                    {TEMPLATES.map(tpl=>(
                      <button key={tpl.name} className="vs-template-card" onClick={()=>applyTemplate(tpl)}>
                        <div className="vs-template-card-icon">{tpl.icon}</div>
                        <div className="vs-template-card-name">{tpl.name}</div>
                        <div className="vs-template-card-desc">{tpl.desc}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label className="vs-modal-label">Folder Name *</label>
            <input className="vs-modal-input" placeholder="e.g. Final Year Project, Assignment 3…" value={fTitle} onChange={e=>setFTitle(e.target.value)}/>

            <label className="vs-modal-label">Description</label>
            <textarea className="vs-modal-textarea" placeholder="What is this folder about?" value={fDesc} onChange={e=>setFDesc(e.target.value)} rows={2}/>

            <label className="vs-modal-label">Tags (comma separated)</label>
            <input className="vs-modal-input" placeholder="e.g. assignment, semester-2, python" value={fTags} onChange={e=>setFTags(e.target.value)}/>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label className="vs-modal-label">Status</label>
                <select className="vs-select" style={{width:"100%",padding:"10px 14px"}} value={fStatus} onChange={e=>setFStatus(e.target.value)}>
                  <option value="active">🟢 Active</option>
                  <option value="in_progress">🟡 In Progress</option>
                  <option value="completed">🔵 Completed</option>
                  <option value="on_hold">🔴 On Hold</option>
                  <option value="archived">⬜ Archived</option>
                </select>
              </div>
              <div>
                <label className="vs-modal-label">Due Date</label>
                <input className="vs-modal-input" type="date" value={fDueDate} onChange={e=>setFDueDate(e.target.value)} style={{marginBottom:0}}/>
              </div>
            </div>

            {/* Initial file upload — only on create */}
            {!editing&&(
              <>
                <label className="vs-modal-label">Add Files (optional — you can add more later)</label>
                <div className="vs-upload-area" style={{position:"relative"}}>
                  <input type="file" multiple accept={ALL_EXT.join(",")} style={{position:"absolute",inset:0,opacity:0,cursor:"pointer"}} onChange={e=>setFFiles(Array.from(e.target.files))}/>
                  <div style={{fontSize:26,marginBottom:4}}>📎</div>
                  <div style={{fontSize:13,color:"var(--text-3)"}}>Click or drag & drop files</div>
                  <div className="vs-upload-hint">Any file type · Max 25MB each · Multiple OK</div>
                  {fFiles.length>0&&(
                    <div className="vs-file-selected">✓ {fFiles.length} file{fFiles.length>1?"s":""} selected: {fFiles.map(f=>f.name).join(", ")}</div>
                  )}
                </div>
              </>
            )}

            {editing&&(
              <>
                <label className="vs-modal-label">Change Note (optional)</label>
                <input className="vs-modal-input" placeholder="What did you change?" value={fChangeNote} onChange={e=>setFChangeNote(e.target.value)}/>
              </>
            )}

            <div className="vs-modal-btn-row">
              <button className="vs-btn-primary" onClick={saveProject} disabled={loading}>
                {loading ? "Saving…" : editing ? "Save Changes" : "📁 Create Folder"}
              </button>
              <button className="vs-btn-ghost" onClick={()=>{setShowModal(false);resetForm();}}>Cancel</button>
            </div>
          </div>
        </div>
      )}</Presence>

      {/* ══ NOTIFICATION PANEL ══ */}
      <Presence show={showNotifPanel} exitVar="--panel-close-dur">{showNotifPanel&&(
        <>
          <div className="vs-drawer-overlay" onClick={()=>setShowNotifPanel(false)}/>
          <div className="vs-notif-panel">
            <div className="vs-notif-header">
              <div className="vs-notif-title">
                Notifications
                {unreadCount>0&&<span style={{marginLeft:10,background:"var(--accent)",color:"var(--on-accent)",borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:700}}>{unreadCount}</span>}
              </div>
              <div className="vs-notif-actions">
                {unreadCount>0&&<button className="vs-notif-clear-btn" onClick={markAllRead}>Mark all read</button>}
                {notifications.length>0&&<button className="vs-notif-clear-btn" onClick={clearAllNotifs}>Clear all</button>}
                <button className="vs-modal-close" onClick={()=>setShowNotifPanel(false)}>✕</button>
              </div>
            </div>
            <div className="vs-notif-list">
              {notifications.length===0?(
                <div className="vs-notif-empty">
                  <div style={{fontSize:36,marginBottom:10,opacity:.4}}>🔔</div>
                  All caught up! No notifications.
                </div>
              ):notifications.map((n,i)=>(
                <div
                  key={n._id}
                  className={"vs-notif-item" + (n.read?"":" unread")}
                  style={{animationDelay:(i*.03) + "s",animation:"vsSlideUp .25s ease both"}}
                  onClick={()=>{ if(!n.read) markNotifRead(n._id); if(n.link){ setActiveNav("projects"); setShowNotifPanel(false); }}}
                >
                  {!n.read&&<div style={{width:6,height:6,background:"var(--accent)",borderRadius:"50%",flexShrink:0,marginTop:5}}/>}
                  <div className="vs-notif-icon">{NOTIF_ICONS[n.type]||"📢"}</div>
                  <div className="vs-notif-body">
                    <div className="vs-notif-item-title">{n.title}</div>
                    <div className="vs-notif-item-msg">{n.message}</div>
                    <div className="vs-notif-item-time">{timeAgo(n.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}</Presence>

      {/* ══ GLOBAL SEARCH ══ */}
      <Presence show={showSearch} exitVar="--modal-close-dur">{showSearch&&(
        <div className="vs-search-overlay" onClick={e=>e.target===e.currentTarget&&setShowSearch(false)}>
          <div className="vs-search-box">
            <div className="vs-search-input-row">
              <span className="vs-search-icon">🔍</span>
              <input
                ref={searchInputRef}
                className="vs-search-field"
                placeholder="Search projects, tags, tech stack…"
                value={searchQuery}
                onChange={e=>setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKey}
              />
              <span className="vs-search-kbd">Esc</span>
            </div>

            {!searchQuery && (
              <div style={{padding:"16px 22px"}}>
                <div className="vs-search-section-label">Recent Projects</div>
                {projects.slice(0,5).map((p,i)=>(
                  <div key={p._id} className={"vs-search-result" + (i===searchFocusIdx?" focused":"")} onClick={()=>{openDrawer(p);setShowSearch(false);}}>
                    <div className="vs-search-result-icon">📁</div>
                    <div>
                      <div className="vs-search-result-title">{p.title}</div>
                      <div className="vs-search-result-sub">📎 {(p.files||[]).length} file{(p.files||[]).length!==1?"s":""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery && searchResults.length===0&&(
              <div className="vs-search-no-results">No folders found for "{searchQuery}"</div>
            )}

            {searchQuery && searchResults.length>0&&(
              <div style={{padding:"8px 14px"}}>
                <div className="vs-search-section-label">{searchResults.length} result{searchResults.length!==1?"s":""}</div>
                {searchResults.map((p,i)=>(
                    <div key={p._id} className={"vs-search-result" + (i===searchFocusIdx?" focused":"")} onClick={()=>{openDrawer(p);setShowSearch(false);}}>
                      <div className="vs-search-result-icon">📁</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div className="vs-search-result-title">{p.title}</div>
                        <div className="vs-search-result-sub">
                          📎 {(p.files||[]).length} file{(p.files||[]).length!==1?"s":""} · {(p.tags||[]).map(t=>"#"+t).join(" ")||"No tags"}
                        </div>
                      </div>
                      <div style={{fontSize:11,color:"var(--text-3)",flexShrink:0}}>
                        {new Date(p.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            <div className="vs-search-hint">
              <span className="vs-search-hint-item"><span className="vs-search-hint-kbd">↑↓</span> Navigate</span>
              <span className="vs-search-hint-item"><span className="vs-search-hint-kbd">↵</span> Open</span>
              <span className="vs-search-hint-item"><span className="vs-search-hint-kbd">Esc</span> Close</span>
            </div>
          </div>
        </div>
      )}</Presence>

      {/* ══ ONBOARDING ══ */}
      <Presence show={showOnboarding} exitVar="--modal-close-dur">{showOnboarding&&(
        <div className="vs-onboard-overlay">
          <div className="vs-onboard-card">
            <div className="vs-onboard-step-dots">
              {ONBOARD_STEPS.map((_,i)=>(
                <div key={i} className={"vs-onboard-dot" + (i===onboardStep?" active":"")}/>
              ))}
            </div>
            <div className="vs-onboard-icon">{ONBOARD_STEPS[onboardStep].icon}</div>
            <div className="vs-onboard-title">{ONBOARD_STEPS[onboardStep].title}</div>
            <div className="vs-onboard-desc">{ONBOARD_STEPS[onboardStep].desc}</div>
            <div className="vs-onboard-btn-row">
              {onboardStep > 0 && (
                <button className="vs-btn-ghost" onClick={()=>setOnboardStep(p=>p-1)}>← Back</button>
              )}
              {onboardStep < ONBOARD_STEPS.length - 1 ? (
                <button className="vs-btn-primary" onClick={()=>setOnboardStep(p=>p+1)}>Next →</button>
              ) : (
                <button className="vs-btn-primary" onClick={finishOnboarding}>Let's go! 🚀</button>
              )}
            </div>
            <button onClick={finishOnboarding} style={{marginTop:16,background:"none",border:"none",color:"var(--text-3)",fontSize:12,cursor:"pointer",fontFamily:"var(--font-body)"}}>
              Skip tour
            </button>
          </div>
        </div>
      )}</Presence>

      {/* ══ SHORTCUTS HINT ══ */}
      {showShortcuts&&(
        <div className="vs-shortcuts-hint" onClick={()=>setShowShortcuts(false)}>
          <span style={{fontWeight:700,color:"var(--text)",marginRight:4}}>Shortcuts</span>
          <span><span className="vs-kbd">N</span> New project</span>
          <span><span className="vs-kbd">/</span> Search</span>
          <span><span className="vs-kbd">Esc</span> Close</span>
          <span><span className="vs-kbd">?</span> This panel</span>
          <span style={{marginLeft:4,opacity:.5}}>✕</span>
        </div>
      )}

      {/* ══ AI SUMMARISER MODAL ══ */}
      <Presence show={!!aiModal} exitVar="--modal-close-dur">{aiModal&&(
        <div className="vs-ai-overlay" onClick={e=>e.target===e.currentTarget&&!aiLoading&&setAiModal(null)}>
          <div className="vs-ai-modal">
            <div className="vs-ai-header">
              <div>
                <div className="vs-ai-title">🤖 AI File Analysis</div>
                <div className="vs-ai-file-name">📎 {aiModal.file.originalName} · {aiModal.projectTitle}</div>
              </div>
              {!aiLoading&&<button className="vs-modal-close" onClick={()=>setAiModal(null)}>✕</button>}
            </div>

            <div className="vs-ai-body">
              {aiLoading&&(
                <div className="vs-ai-loading">
                  <div className="vs-ai-spinner"/>
                  <div className="vs-ai-loading-text">Analysing your file with Claude AI…</div>
                  <div style={{fontSize:12,color:"var(--text-3)"}}>This may take a few seconds</div>
                </div>
              )}

              {!aiLoading&&aiResult&&(
                <>
                  {/* SUMMARY */}
                  <div className="vs-ai-section">
                    <div className="vs-ai-section-title">📋 Summary</div>
                    <div className="vs-ai-summary-text">{aiResult.summary}</div>
                  </div>

                  {/* META ROW */}
                  {!aiResult.isRaw&&(
                    <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
                      <div style={{background:"var(--surface-2)",borderRadius:10,padding:"8px 14px",fontSize:12}}>
                        <span style={{color:"var(--text-3)",fontWeight:700,textTransform:"uppercase",fontSize:10,letterSpacing:".5px"}}>File Type</span>
                        <div style={{color:"var(--text)",fontWeight:600,marginTop:2}}>{aiResult.fileType}</div>
                      </div>
                      <div style={{background:"var(--surface-2)",borderRadius:10,padding:"8px 14px",fontSize:12}}>
                        <span style={{color:"var(--text-3)",fontWeight:700,textTransform:"uppercase",fontSize:10,letterSpacing:".5px"}}>Complexity</span>
                        <div style={{color: aiResult.complexity==="Complex"?"var(--danger)":aiResult.complexity==="Moderate"?"var(--warning)":"var(--success)",fontWeight:600,marginTop:2}}>{aiResult.complexity}</div>
                      </div>
                    </div>
                  )}

                  {/* KEY POINTS */}
                  {aiResult.keyPoints&&aiResult.keyPoints.length>0&&(
                    <div className="vs-ai-section">
                      <div className="vs-ai-section-title">⚡ Key Points</div>
                      <div style={{background:"var(--surface-2)",borderRadius:12,padding:"4px 12px"}}>
                        {aiResult.keyPoints.map((pt,i)=>(
                          <div key={i} className="vs-ai-point">
                            <div className="vs-ai-point-dot"/>
                            <div className="vs-ai-point-text">{pt}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TAGS */}
                  {aiResult.tags&&aiResult.tags.length>0&&(
                    <div className="vs-ai-section">
                      <div className="vs-ai-section-title">🏷️ Suggested Tags</div>
                      <div>{aiResult.tags.map((tag,i)=><span key={i} className="vs-ai-chip">#{tag}</span>)}</div>
                    </div>
                  )}

                  <div style={{marginTop:16,display:"flex",gap:10}}>
                    <button className="vs-ai-btn" onClick={()=>summariseFile(aiModal.file, aiModal.projectTitle)}>↻ Re-analyse</button>
                    <button className="vs-btn-ghost" style={{fontSize:12}} onClick={()=>setAiModal(null)}>Close</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}</Presence>

      {/* ── MOBILE BOTTOM NAV ── */}
      <nav className="vs-bottom-nav" style={{display:"none"}}>
        {[
          {key:"projects",  icon:"⊞", label:"Home"},
          {key:"collab",    icon:"🤝", label:"Collab"},
          {key:"analytics", icon:"📊", label:"Stats"},
          {key:"teams",     icon:"👥", label:"Teams"},
          {key:"settings",  icon:"⚙️", label:"More"},
        ].map(n=>(
          <button key={n.key} className={"vs-bottom-nav-btn" + (activeNav===n.key?" active":"")} onClick={()=>setActiveNav(n.key)}>
            <span className="bn-icon">{n.icon}</span>
            {n.label}
            {n.key==="collab"&&pendingCount>0&&<span className="vs-nav-badge" style={{fontSize:8,padding:"1px 4px"}}>{pendingCount}</span>}
          </button>
        ))}
      </nav>

    </div>
  );
}

export default Dashboard;